import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import projectInitialBalanceService from '../services/projectInitialBalanceService';
import projectsService from '../services/projectsService';
import paymentConditionsService from '../services/paymentConditionsService';
import cardRatesService from '../services/cardRatesService';
import { Plus, Trash2, Edit, Save, X, Calendar, DollarSign, AlertCircle, AlertTriangle, CreditCard, Calculator, ChevronRight, ChevronLeft, Check, Eye, ArrowDownRight, RefreshCw, Tag, Sliders, FileText, Info, Coins, Receipt, Clock, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ExpenseAbatementModal from './ExpenseAbatementModal';
import InitialBalanceReceiptModal from './budget/InitialBalanceReceiptModal';
import expenseAbatesService from '../services/expenseAbatesService';
import CurrencyInput from './CurrencyInput';
import ConfirmModal from './ConfirmModal';

const ProjectInitialBalances = ({ projectId, estimatedBudget, projectName, clientName, onClose }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editingBalance, setEditingBalance] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [detailsModal, setDetailsModal] = useState({ open: false, balance: null, entries: null, loading: false });
  const [abateModalOpen, setAbateModalOpen] = useState(false);
  const [projectAbates, setProjectAbates] = useState([]);
  const [extraIncomes, setExtraIncomes] = useState([]);
  const [adjustModal, setAdjustModal] = useState({ open: false, balance: null, loading: false });
  const [receiptModal, setReceiptModal] = useState({ open: false, data: null });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ open: false, balanceId: null });
  const [cancelAbateConfirmModal, setCancelAbateConfirmModal] = useState({ open: false, abateId: null });
  const [deleteAbateConfirmModal, setDeleteAbateConfirmModal] = useState({ open: false, abateId: null });
  const [ratePaidBy, setRatePaidBy] = useState('cliente');
  const [cardRates, setCardRates] = useState([]);
  const [entryCardType, setEntryCardType] = useState('CREDIT');
  const [entryIsParcelado, setEntryIsParcelado] = useState(false);
  const [installmentCardType, setInstallmentCardType] = useState('CREDIT');
  const [installmentIsParcelado, setInstallmentIsParcelado] = useState(false);
  const [generatedEntryCardInstallments, setGeneratedEntryCardInstallments] = useState([]);
  const [generatedInstallmentCardInstallments, setGeneratedInstallmentCardInstallments] = useState([]);
  const [confirmAllCardModalOpen, setConfirmAllCardModalOpen] = useState(false);

  const getInitialFormData = () => ({
    total_amount: estimatedBudget || '',
    reference_date: '',
    description: '',
    has_entry: false,
    entry_amount: '',
    entry_date: '',
    entry_payment_type: 'cash',
    entry_splits_count: 1,
    entry_splits: [],
    payment_type: 'cash',
    installments: 1,
    first_installment_date: '',
    installment_payment_type: 'cash',
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // Converter string formatada para número (1.234,56 -> 1234.56, 705.16 -> 705.16)
  const parseFormattedCurrency = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    const str = String(value).trim();
    if (str.includes(',')) {
      const cleaned = str.replace(/\./g, '').replace(',', '.');
      return parseFloat(cleaned) || 0;
    }
    return parseFloat(str) || 0;
  };

  // Formatar número para exibição brasileira (1234.56 -> 1.234,56)
  const formatBRL = (value) => {
    if (!value && value !== 0) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    if (num === 0) return '0,00';

    // Formata com 2 casas decimais
    const fixed = num.toFixed(2);
    const [intPart, decPart] = fixed.split('.');

    // Adiciona separadores de milhar
    const withSeparators = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `${withSeparators},${decPart}`;
  };

  // Handler para campos de moeda
  const handleCurrencyChange = (e, fieldName) => {
    const rawValue = e.target.value;
    // Aceita apenas números, vírgula e ponto
    if (/^[\d.,]*$/.test(rawValue) || rawValue === '') {
      setFormData(prev => ({ ...prev, [fieldName]: rawValue }));
    }
  };

  // Handler para campos de moeda - formata quando sai do campo
  const handleCurrencyBlur = (e, fieldName) => {
    const inputValue = e.target.value;
    const numValue = parseFormattedCurrency(inputValue);
    setFormData(prev => ({ ...prev, [fieldName]: numValue }));
    e.target.value = formatBRL(numValue);
  };

  // Handler para focar no campo - salva valor formatado
  const handleCurrencyFocus = (e) => {
    const numValue = parseFormattedCurrency(e.target.value);
    if (numValue > 0) {
      e.target.value = String(numValue).replace('.', ',');
    }
  };

  // Handler para splits de entrada - parse imediato no onChange
  const handleSplitCurrencyChange = (index, field, e) => {
    const rawValue = e.target.value;
    const numValue = parseFormattedCurrency(rawValue);
    setFormData(prev => {
      const newSplits = [...prev.entry_splits];
      newSplits[index] = { ...newSplits[index], [field]: numValue };
      return { ...prev, entry_splits: newSplits };
    });
  };

  // Handler para splits de entrada - apenas no onBlur para formatar display
  const handleSplitCurrencyBlur = (index, field, e) => {
    const inputValue = e.target.value;
    const numValue = parseFormattedCurrency(inputValue);
    setFormData(prev => {
      const newSplits = [...prev.entry_splits];
      newSplits[index] = { ...newSplits[index], [field]: numValue };
      return { ...prev, entry_splits: newSplits };
    });
    e.target.value = formatBRL(numValue);
  };

  // Handler para installment splits - parse imediato no onChange
  const handleInstallmentCurrencyChange = (index, e) => {
    const rawValue = e.target.value;
    const numValue = parseFormattedCurrency(rawValue);
    handleInstallmentSplitChange(index, 'amount', numValue);
  };

  // Handler para installment splits - apenas no onBlur para formatar display
  const handleInstallmentCurrencyBlur = (index, e) => {
    const inputValue = e.target.value;
    const numValue = parseFormattedCurrency(inputValue);
    handleInstallmentSplitChange(index, 'amount', numValue);
    e.target.value = formatBRL(numValue);
  };

  // State para installments do passo 3
  const [installmentSplits, setInstallmentSplits] = useState([]);

  // Inicializar installmentSplits quando installments muda (apenas se a quantidade mudou ou se estiver vazio)
  useEffect(() => {
    if (currentStep === 3 && formData.installments > 1) {
      const numInstallments = parseInt(formData.installments) || 1;
      if (installmentSplits.length !== numInstallments) {
        const remaining = calculateRemainingAmount();
        const baseAmount = Math.floor(remaining / numInstallments * 100) / 100;
        const firstAmount = Math.round((remaining - (baseAmount * (numInstallments - 1))) * 100) / 100;
        
        const splits = [];
        for (let i = 0; i < numInstallments; i++) {
          splits.push({
            amount: i === 0 ? firstAmount : baseAmount,
            date: '',
            payment_type: formData.payment_type || 'cash',
          });
        }
        setInstallmentSplits(splits);
      }
    }
  }, [formData.installments, currentStep]);

  // Recalcular divisão igualitária das parcelas com base no saldo restante
  const handleRecalculateEqualSplits = () => {
    const remaining = calculateRemainingAmount();
    const numInstallments = parseInt(formData.installments) || 1;
    if (numInstallments <= 1) return;
    const baseAmount = Math.floor(remaining / numInstallments * 100) / 100;
    const firstAmount = Math.round((remaining - (baseAmount * (numInstallments - 1))) * 100) / 100;

    setInstallmentSplits(prev =>
      prev.map((split, i) => ({
        ...split,
        amount: i === 0 ? firstAmount : baseAmount
      }))
    );
    toast.success('Parcelas recalculadas com divisão exata!');
  };

  // Handler para mudar valor de parcela
  const handleInstallmentSplitChange = (index, field, value) => {
    setInstallmentSplits(prev => {
      const newSplits = [...prev];
      newSplits[index] = { ...newSplits[index], [field]: value };
      return newSplits;
    });
  };

  // Gerar datas das parcelas automaticamente (1 mês entre cada)
  const handleGenerateInstallmentDates = () => {
    if (!formData.first_installment_date) {
      toast.error('Informe a data da 1ª parcela primeiro');
      return;
    }
    const firstDate = new Date(formData.first_installment_date + 'T00:00:00');
    const originalDay = firstDate.getDate();
    const updatedSplits = installmentSplits.map((split, index) => {
      let newDate = new Date(firstDate);
      newDate.setMonth(firstDate.getMonth() + index);

      // Se o dia não existir no mês destino (ex: 31/fev), vai para o último dia válido
      if (newDate.getDate() !== originalDay) {
        newDate = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
      }

      return {
        ...split,
        date: newDate.toISOString().split('T')[0]
      };
    });
    setInstallmentSplits(updatedSplits);
    toast.success('Datas geradas automaticamente!');
  };

  // Calcular soma dos splits de parcela com arredondamento preciso
  const calculateInstallmentSplitsTotal = () => {
    const raw = installmentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
    return Math.round(raw * 100) / 100;
  };

  // Buscar saldos iniciais
  const { data: balancesData, isLoading } = useQuery({
    queryKey: ['projectInitialBalances', projectId],
    queryFn: () => projectInitialBalanceService.getBalances(projectId),
    enabled: !!projectId,
  });

  // Buscar condições de pagamento globais (todas as condições cadastradas)
  const { data: paymentConditionsData } = useQuery({
    queryKey: ['paymentConditions'],
    queryFn: () => paymentConditionsService.getAllConditions({ is_active: true }),
  });

  const balances = balancesData?.data?.data || balancesData?.data || [];
  const paymentConditions = paymentConditionsData?.data?.data || paymentConditionsData?.data || [];

  // Estado para fornecedores
  const [suppliers, setSuppliers] = useState([]);

  // Buscar fornecedores para pagamento direto
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const api = (await import('../services/api')).default;
        const response = await api.get('/suppliers');
        setSuppliers(response.data.data || response.data || []);
      } catch (error) {
        console.error('Erro ao carregar fornecedores:', error);
      }
    };
    fetchSuppliers();
  }, []);

  // Buscar taxas de cartão
  useEffect(() => {
    const loadCardRates = async () => {
      try {
        const data = await cardRatesService.getAllRates();
        setCardRates(data || []);
      } catch (error) {
        console.error('Erro ao carregar taxas de cartão:', error);
      }
    };
    loadCardRates();
  }, []);

  // Calcular valor restante
  const calculateRemainingAmount = () => {
    const total = parseFloat(formData.total_amount) || 0;
    let entry = 0;

    if (formData.has_entry) {
      // Se é pagamento com cartão parcelado, usar valor original das parcelas geradas
      if (formData.entry_payment_type === 'card' && generatedEntryCardInstallments.length > 0) {
        entry = generatedEntryCardInstallments.reduce((sum, inst) => sum + inst.originalValue, 0);
      } else if (parseInt(formData.entry_splits_count) > 1) {
        // Somar valores dos splits da entrada (para não-cartão)
        entry = formData.entry_splits.reduce((sum, split) => sum + (parseFloat(split.amount) || 0), 0);
      } else {
        entry = parseFloat(formData.entry_amount) || 0;
      }
    }

    return total - entry;
  };

  // Calcular valor da parcela (primeira parcela recebe o resto)
  const calculateInstallmentAmount = () => {
    const remaining = calculateRemainingAmount();
    const installments = parseInt(formData.installments) || 1;
    if (installments <= 0) return 0;
    return Math.floor(remaining / installments * 100) / 100;
  };

  // Calcular resto (diferença que vai para primeira parcela)
  const calculateRemainder = () => {
    const remaining = calculateRemainingAmount();
    const installments = parseInt(formData.installments) || 1;
    if (installments <= 0) return 0;
    const installmentAmount = calculateInstallmentAmount();
    return remaining - (installmentAmount * installments);
  };

  // Mutation para criar saldo
  const createMutation = useMutation({
    mutationFn: (data) => projectInitialBalanceService.createBalance(projectId, data),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Saldo inicial criado com sucesso!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      resetForm();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao criar saldo inicial');
    },
  });

  // Mutation para atualizar saldo
  const updateMutation = useMutation({
    mutationFn: ({ balanceId, data }) => projectInitialBalanceService.updateBalance(projectId, balanceId, data),
    onSuccess: (res) => {
      toast.success('Saldo atualizado com sucesso!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      setEditingBalance(null);
      resetForm();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao atualizar saldo');
    },
  });

  // Mutation para deletar saldo
  const deleteMutation = useMutation({
    mutationFn: (balanceId) => projectInitialBalanceService.deleteBalance(projectId, balanceId),
    onSuccess: (res) => {
      toast.success('Saldo deletado com sucesso!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao deletar saldo');
    },
  });

  // Mutation para confirmar recebimento de parcela
  const confirmReceiptMutation = useMutation({
    mutationFn: ({ balanceId, incomeId }) => projectInitialBalanceService.confirmParcelReceipt(projectId, balanceId, incomeId),
    onSuccess: async (res) => {
      toast.success(res.data.message || 'Parcela confirmada e registrada no caixa!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      // Recarregar dados do modal de detalhes se estiver aberto
      if (detailsModal.open && detailsModal.balance) {
        try {
          const response = await projectInitialBalanceService.getEntries(projectId, detailsModal.balance.id);
          setDetailsModal(prev => ({ ...prev, entries: response.data.data, loading: false }));
        } catch (error) {
          console.error('Erro ao recarregar detalhes:', error);
        }
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao confirmar recebimento');
    },
  });

  // FEATURE 02: Mutation para confirmar recebimento com valor diferente
  const confirmParcelRefactoredMutation = useMutation({
    mutationFn: ({ balanceId, expectedIncomeId, receivedAmount, paymentDate, paymentMethod, supplierId, activate_fractioning, idempotencyKey }) =>
      projectInitialBalanceService.confirmParcelReceiptRefactored(projectId, balanceId, { expectedIncomeId, receivedAmount, paymentDate, paymentMethod, supplierId, activate_fractioning, idempotencyKey }),
    onSuccess: async (res) => {
      toast.success(res.data.message || 'Parcela confirmada com ajuste!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      setPartialReceiptModal({ open: false, parcel: null, loading: false });
      // Recarregar dados do modal de detalhes se estiver aberto
      if (detailsModal.open && detailsModal.balance) {
        try {
          const response = await projectInitialBalanceService.getEntries(projectId, detailsModal.balance.id);
          setDetailsModal(prev => ({ ...prev, entries: response.data.data, loading: false }));
        } catch (error) {
          console.error('Erro ao recarregar detalhes:', error);
        }
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao confirmar recebimento');
    },
  });

  // Mutation para confirmar todas as parcelas do cartão em lote
  const confirmAllCardMutation = useMutation({
    mutationFn: ({ balanceId }) => projectInitialBalanceService.confirmAllCardInstallments(projectId, balanceId),
    onSuccess: async (res) => {
      toast.success(res.data?.message || 'Parcelas de cartão confirmadas com sucesso!');
      setConfirmAllCardModalOpen(false);
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      if (detailsModal.open && detailsModal.balance) {
        try {
          const response = await projectInitialBalanceService.getEntries(projectId, detailsModal.balance.id);
          setDetailsModal(prev => ({ ...prev, entries: response.data.data, loading: false }));
        } catch (error) {
          console.error('Erro ao recarregar detalhes:', error);
        }
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Erro ao confirmar parcelas de cartão');
    },
  });

  // Mutation para alternar posse física do cheque em mãos
  const toggleChequeInHandMutation = useMutation({
    mutationFn: ({ balanceId, expectedIncomeId, inHand }) =>
      projectInitialBalanceService.toggleChequeInHand(projectId, balanceId, { expectedIncomeId, inHand }),
    onSuccess: async (res) => {
      toast.success(res.data?.message || 'Status do cheque atualizado!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      if (detailsModal.open && detailsModal.balance) {
        try {
          const response = await projectInitialBalanceService.getEntries(projectId, detailsModal.balance.id);
          setDetailsModal(prev => ({ ...prev, entries: response.data.data, loading: false }));
        } catch (error) {
          console.error('Erro ao recarregar detalhes:', error);
        }
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Erro ao atualizar status do cheque');
    },
  });

  // FEATURE 01: Mutation para desconfirmar recebimento
  const unconfirmReceiptMutation = useMutation({
    mutationFn: ({ balanceId, incomeId, reason, newDueDate }) =>
      projectInitialBalanceService.unconfirmParcelReceipt(projectId, balanceId, { incomeId, reason, newDueDate }),
    onSuccess: async (res) => {
      toast.success(res.data.message || 'Parcela desconfirmada com sucesso!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      setUnconfirmModal({ open: false, parcel: null, loading: false });
      // Recarregar dados do modal de detalhes se estiver aberto
      if (detailsModal.open && detailsModal.balance) {
        try {
          const response = await projectInitialBalanceService.getEntries(projectId, detailsModal.balance.id);
          setDetailsModal(prev => ({ ...prev, entries: response.data.data, loading: false }));
        } catch (error) {
          console.error('Erro ao recarregar detalhes:', error);
        }
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao desconfirmar recebimento');
    },
  });

  // FEATURE 03: Mutation para alterar data de vencimento
  const changeDueDateMutation = useMutation({
    mutationFn: ({ balanceId, expectedIncomeId, newDueDate, reason, idempotencyKey }) =>
      projectInitialBalanceService.changeParcelDueDate(projectId, balanceId, { expectedIncomeId, newDueDate, reason, idempotencyKey }),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Data de vencimento alterada!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      setChangeDateModal({ open: false, parcel: null, loading: false });
      if (detailsModal.balance) {
        handleViewDetails(detailsModal.balance);
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao alterar data');
    },
  });

  // FEATURE: Mutation para aplicar desconto
  const applyDiscountMutation = useMutation({
    mutationFn: ({ balanceId, expectedIncomeId, discountAmount }) =>
      projectInitialBalanceService.applyDiscount(projectId, balanceId, { expectedIncomeId, discountAmount }),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Desconto aplicado!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      setDiscountModal({ open: false, parcel: null, loading: false });
      if (detailsModal.balance) {
        handleViewDetails(detailsModal.balance);
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao aplicar desconto');
    },
  });

  // FEATURE 02: Modal para recebimento parcial
  const [partialReceiptModal, setPartialReceiptModal] = useState({ open: false, parcel: null, loading: false });
  const [receiptPaymentMethod, setReceiptPaymentMethod] = useState('pix');
  const [activateFractioning, setActivateFractioning] = useState(false);
  const [hasAmountDifference, setHasAmountDifference] = useState(false);

  // FEATURE 01: Modal para desconfirmação
  const [unconfirmModal, setUnconfirmModal] = useState({ open: false, parcel: null, loading: false });

  // FEATURE 03: Modal para alterar data
  const [changeDateModal, setChangeDateModal] = useState({ open: false, parcel: null, loading: false });

  // FEATURE: Modal para aplicar desconto
  const [discountModal, setDiscountModal] = useState({ open: false, parcel: null, loading: false });

  // Modal para compensação manual de cheque em mãos
  const [compensateModal, setCompensateModal] = useState({ open: false, parcel: null });

  const [showAddSplit, setShowAddSplit] = useState(false);
  const [newSplit, setNewSplit] = useState({ amount: '', date: '', payment_type: 'cash', supplier_id: null });

  // Fetch project abates
  useEffect(() => {
    if (projectId) {
      fetchProjectAbates();
    }
  }, [projectId]);

  const fetchProjectAbates = async () => {
    try {
      const response = await expenseAbatesService.getAbatesByProject(projectId);
      setProjectAbates(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar abates:', error);
    }
  };

  // Fetch project extra incomes
  useEffect(() => {
    if (projectId) {
      fetchExtraIncomes();
    }
  }, [projectId]);

  const fetchExtraIncomes = async () => {
    try {
      const response = await projectsService.getProjectExtraIncomes(projectId);
      setExtraIncomes(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar recebimentos extras:', error);
    }
  };

  // Mark abate as paid mutation
  const markAbatePaidMutation = useMutation({
    mutationFn: ({ id, paymentData }) => expenseAbatesService.markAbateAsPaid(id, paymentData),
    onSuccess: (res) => {
      toast.success(res.message || 'Abate marcado como pago!');
      fetchProjectAbates();
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao marcar abate como pago');
    },
  });

  // Cancel abate mutation
  const cancelAbateMutation = useMutation({
    mutationFn: (id) => expenseAbatesService.cancelAbate(id),
    onSuccess: (res) => {
      toast.success(res.message || 'Abate cancelado!');
      fetchProjectAbates();
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao cancelar abate');
    },
  });

  const addSplitMutation = useMutation({
    mutationFn: ({ balanceId, splitData }) => projectInitialBalanceService.addEntrySplit(projectId, balanceId, splitData),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Parcela de entrada extra adicionada!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      setShowAddSplit(false);
      setNewSplit({ amount: '', date: '', payment_type: 'cash', supplier_id: null });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao adicionar parcela');
    },
  });

  const recalculateMutation = useMutation({
    mutationFn: (balanceId) => projectInitialBalanceService.recalculateExpectedAmounts(projectId, balanceId),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Parcelas recalculadas com sucesso!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      handleViewDetails(detailsModal.balance);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao recalcular parcelas');
    },
  });

  const handleRecalculate = (balanceId) => {
    recalculateMutation.mutate(balanceId);
  };

  const adjustMutation = useMutation({
    mutationFn: async (balanceId) => {
      await projectInitialBalanceService.adjustInstallmentAmounts(projectId, balanceId);
      return projectInitialBalanceService.getEntries(projectId, balanceId);
    },
    onSuccess: (res) => {
      toast.success('Ajuste realizado com sucesso!');
      queryClient.invalidateQueries(['projectInitialBalances', projectId]);
      setDetailsModal(prev => ({ ...prev, entries: res.data.data }));
      setAdjustModal({ open: false, balance: null, loading: false });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao realizar ajuste');
      setAdjustModal({ open: false, balance: null, loading: false });
    },
  });

  const handleOpenAdjust = (balance) => {
    setAdjustModal({ open: true, balance, loading: false });
  };

  const handleAdjust = () => {
    if (!adjustModal.balance) return;
    setAdjustModal(prev => ({ ...prev, loading: true }));
    adjustMutation.mutate(adjustModal.balance.id);
  };

  const handleAddSplit = (e) => {
    e.preventDefault();
    if (!editingBalance) return;
    if (!newSplit.amount || !newSplit.date) {
      toast.error('Preencha valor e data da parcela');
      return;
    }
    const isDirectPayment = newSplit.payment_type && newSplit.payment_type.endsWith('_direct');
    if (isDirectPayment && !newSplit.supplier_id) {
      toast.error('Fornecedor é obrigatório para pagamento direto ao fornecedor.');
      return;
    }
    addSplitMutation.mutate({
      balanceId: editingBalance.id,
      splitData: {
        amount: parseFloat(newSplit.amount),
        date: newSplit.date,
        payment_type: newSplit.payment_type,
        supplier_id: isDirectPayment ? newSplit.supplier_id : null,
      },
    });
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setIsEditing(false);
    setEditingBalance(null);
    setCurrentStep(1);
    setShowAddSplit(false);
    setNewSplit({ amount: '', date: '', payment_type: 'cash', supplier_id: null });
    setInstallmentSplits([]);
    setRatePaidBy('cliente');
    setEntryCardType('CREDIT');
    setEntryIsParcelado(false);
    setInstallmentCardType('CREDIT');
    setInstallmentIsParcelado(false);
    setGeneratedEntryCardInstallments([]);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };

      // Para cartão parcelado, NÃO criar splits automaticamente
      // Splits de cartão são criados apenas quando clicar em "Gerar Parcelas"
      if (name === 'entry_splits_count' && prev.entry_payment_type !== 'card') {
        const count = parseInt(newValue) || 1;
        const newSplits = [];

        for (let i = 0; i < count; i++) {
          const existingSplit = prev.entry_splits[i] || { amount: '', date: '', payment_type: 'cash' };
          newSplits.push(existingSplit);
        }
        updated.entry_splits = newSplits;
      }

      // Limpar fornecedor se mudar para tipo não-direto
      if (name === 'entry_payment_type' && !newValue.endsWith('_direct')) {
        updated.entry_supplier_id = null;
      }

      // Se mudou payment_type para cartão, limpar splits e generated
      if (name === 'entry_payment_type' && newValue === 'card') {
        updated.entry_splits = [];
        setGeneratedEntryCardInstallments([]);
      }

      return updated;
    });
  };

  const handleSplitChange = (index, field, value) => {
    setFormData(prev => {
      const newSplits = [...prev.entry_splits];
      newSplits[index] = { ...newSplits[index], [field]: value };
      return { ...prev, entry_splits: newSplits };
    });
  };

  const handleNextStep = (e) => {
    // Prevenir submit do formulário
    e?.preventDefault?.();

    // Validar campos obrigatórios da etapa atual
    if (currentStep === 1) {
      if (!formData.total_amount || !formData.reference_date) {
        toast.error('Por favor, preencha o Valor Total e a Data de Referência');
        return;
      }
    }
    if (currentStep === 2) {
      if (formData.has_entry) {
        const splitsCount = parseInt(formData.entry_splits_count) || 1;
        if (splitsCount === 1) {
          if (!formData.entry_amount || !formData.entry_date) {
            toast.error('Por favor, preencha valor e data da entrada');
            return;
          }
          if (parseFloat(formData.entry_amount) > parseFloat(formData.total_amount)) {
            toast.error(`Valor da entrada (${formatCurrency(parseFloat(formData.entry_amount))}) não pode ser maior que o total do projeto (${formatCurrency(parseFloat(formData.total_amount))})`);
            return;
          }
        } else {
          const totalSplits = formData.entry_splits.reduce((sum, split) => sum + (parseFloat(split.amount) || 0), 0);
          const projectTotal = parseFloat(formData.total_amount) || 0;
          if (totalSplits > projectTotal) {
            toast.error(`Soma das parcelas (${formatCurrency(totalSplits)}) não pode exceder o valor total do projeto (${formatCurrency(projectTotal)})`);
            return;
          }
          if (totalSplits === 0) {
            toast.error('Preencha pelo menos uma parcela de entrada');
            return;
          }
          for (let i = 0; i < splitsCount; i++) {
            if (!formData.entry_splits[i]?.amount || parseFloat(formData.entry_splits[i]?.amount) <= 0) {
              toast.error(`Parcela ${i + 1}: preencha um valor maior que zero`);
              return;
            }
            if (!formData.entry_splits[i]?.date) {
              toast.error(`Parcela ${i + 1}: preencha a data`);
              return;
            }
          }
        }
      }
    }
    if (currentStep === 3) {
      if (formData.payment_type === 'card' && generatedInstallmentCardInstallments.length > 0) {
        const totalFinal = Math.round(generatedInstallmentCardInstallments.reduce((sum, i) => sum + (parseFloat(i.finalValue) || 0), 0) * 100) / 100;
        const remaining = calculateRemainingAmount();
        const cardRate = getCardRate(installmentCardType, parseInt(formData.installments) || 1);
        const expectedFinal = ratePaidBy === 'cliente' && cardRate > 0
          ? Math.round(remaining * (1 + cardRate) * 100) / 100
          : remaining;
        const diff = Math.round(Math.abs(totalFinal - expectedFinal) * 100) / 100;
        if (diff > 0.01) {
          if (totalFinal > expectedFinal) {
            toast.error(`A soma das parcelas (${formatCurrency(totalFinal)}) extrapolou o valor total (${formatCurrency(expectedFinal)}). Ajuste os valores.`);
          } else {
            toast.error(`A soma das parcelas (${formatCurrency(totalFinal)}) não confere com o valor total (${formatCurrency(expectedFinal)}). Ajuste os valores.`);
          }
          return;
        }
      } else if (installmentSplits.length > 0) {
        const splitsTotal = calculateInstallmentSplitsTotal();
        const remaining = calculateRemainingAmount();
        const diff = Math.abs(splitsTotal - remaining);
        if (diff > 0.01) {
          toast.error(`Soma das parcelas (${formatCurrency(splitsTotal)}) não confere com valor esperado (${formatCurrency(remaining)}). Ajuste os valores.`);
          return;
        }
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const remainingAmount = calculateRemainingAmount();
    const installmentAmount = calculateInstallmentAmount();

    // Validar soma dos installment splits se existirem
    if (formData.payment_type === 'card' && generatedInstallmentCardInstallments.length > 0) {
      const totalFinal = Math.round(generatedInstallmentCardInstallments.reduce((sum, i) => sum + (parseFloat(i.finalValue) || 0), 0) * 100) / 100;
      const cardRate = getCardRate(installmentCardType, parseInt(formData.installments) || 1);
      const expectedFinal = ratePaidBy === 'cliente' && cardRate > 0
        ? Math.round(remainingAmount * (1 + cardRate) * 100) / 100
        : remainingAmount;
      const diff = Math.round(Math.abs(totalFinal - expectedFinal) * 100) / 100;
      if (diff > 0.01) {
        if (totalFinal > expectedFinal) {
          toast.error(`A soma das parcelas (${formatCurrency(totalFinal)}) extrapolou o valor total (${formatCurrency(expectedFinal)}). Ajuste os valores.`);
        } else {
          toast.error(`A soma das parcelas (${formatCurrency(totalFinal)}) não confere com o valor total (${formatCurrency(expectedFinal)}). Ajuste os valores.`);
        }
        return;
      }
    } else if (installmentSplits.length > 0) {
      const splitsTotal = calculateInstallmentSplitsTotal();
      const diff = Math.abs(splitsTotal - remainingAmount);
      if (diff > 0.01) {
        toast.error(`Soma das parcelas (${formatCurrency(splitsTotal)}) não confere com valor esperado (${formatCurrency(remainingAmount)}). Ajuste os valores.`);
        return;
      }
      
      // Verificar se todas têm data
      for (let i = 0; i < installmentSplits.length; i++) {
        if (!installmentSplits[i].date) {
          toast.error(`Parcela ${i + 1}: preencha a data`);
          return;
        }
        if (!installmentSplits[i].amount || installmentSplits[i].amount <= 0) {
          toast.error(`Parcela ${i + 1}: preencha um valor maior que zero`);
          return;
        }
      }
    }

    const { entry_splits_count, ...restFormData } = formData;

    const splitsCount = parseInt(entry_splits_count) || 1;
    let entryAmount = formData.has_entry ? formData.entry_amount || 0 : 0;
    let entrySplits = [];

    if (splitsCount > 1) {
      entrySplits = formData.entry_splits
        .filter(s => s.amount && s.date)
        .map(s => ({
          amount: parseFormattedCurrency(s.amount) || 0,
          date: s.date,
          payment_type: s.payment_type || 'cash',
        }));
      entryAmount = entrySplits.reduce((sum, s) => sum + s.amount, 0);
    }

    const data = {
      ...restFormData,
      total_amount: formData.total_amount,
      entry_amount: formData.has_entry ? entryAmount : null,
      entry_paid: formData.has_entry,
      installments: parseInt(formData.installments),
      installment_amount: installmentAmount,
      installments_paid: 0,
      entry_payment_type: formData.entry_payment_type || 'cash',
      installment_payment_type: formData.installment_payment_type || 'cash',
      entry_splits: entrySplits.length > 0 ? entrySplits : null,
      installment_splits: installmentSplits.length > 0 ? installmentSplits : null,
      rate_paid_by: ratePaidBy,
    };

    if (editingBalance) {
      updateMutation.mutate({ balanceId: editingBalance.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (balance) => {
    setEditingBalance(balance);
    setFormData({
      total_amount: balance.total_amount,
      reference_date: balance.reference_date,
      description: balance.description || '',
      has_entry: balance.entry_amount > 0,
      entry_amount: balance.entry_amount || '',
      entry_date: balance.entry_date || '',
      payment_type: balance.payment_type || 'cash',
      installments: balance.installments || 1,
      first_installment_date: balance.first_installment_date || '',
      entry_splits: balance.entry_splits || [],
    });
    setIsEditing(true);
    setCurrentStep(1);
    setShowAddSplit(false);
    setNewSplit({ amount: '', date: '', payment_type: 'cash', supplier_id: null });
    setInstallmentSplits(balance.installment_splits || []);
    setRatePaidBy(balance.rate_paid_by || 'cliente');
    setEntryCardType('CREDIT');
    setEntryIsParcelado(false);
    setInstallmentCardType('CREDIT');
    setInstallmentIsParcelado(false);
  };

  const handleDelete = (balanceId) => {
    setDeleteConfirmModal({ open: true, balanceId });
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmModal.balanceId) {
      deleteMutation.mutate(deleteConfirmModal.balanceId);
    }
    setDeleteConfirmModal({ open: false, balanceId: null });
  };

  const handleViewDetails = async (balance) => {
    setDetailsModal({ open: true, balance, entries: null, loading: true });
    try {
      const response = await projectInitialBalanceService.getEntries(projectId, balance.id);
      setDetailsModal(prev => ({ ...prev, entries: response.data.data, loading: false }));
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
      setDetailsModal({ open: false, balance: null, entries: null, loading: false });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      PARTIAL: 'bg-blue-100 text-blue-800',
      RECEIVED: 'bg-green-100 text-green-800',
      COMPLETED: 'bg-green-100 text-green-800',
    };
    const labels = {
      PENDING: 'Pendente',
      PARTIAL: 'Parcial',
      RECEIVED: 'Concluído',
      COMPLETED: 'Concluído',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.PENDING}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPaymentConditionLabel = (condition) => {
    if (!condition) return '-';
    return condition.name || condition.condition_name || condition.custom_name || 'Condição Personalizada';
  };

  const isCardPayment = (paymentType) => {
    return paymentType === 'card' || paymentType === 'credit_card' || paymentType === 'debit_card';
  };

  const getCardRate = (cardType, installments) => {
    if (!cardRates || cardRates.length === 0) return 0;
    const rate = cardRates.find(r =>
      r.card_brand?.toUpperCase() === cardType?.toUpperCase() &&
      r.installments === installments
    );
    return rate ? rate.rate_percentage / 100 : 0;
  };

  const generateEntryCardInstallments = () => {
    const entryAmount = parseFormattedCurrency(formData.entry_amount);
    const installmentsCount = parseInt(formData.entry_splits_count) || 1;
    if (installmentsCount <= 0 || entryAmount <= 0) return;

    const cardRate = getCardRate(entryCardType, installmentsCount);
    const ratePercentage = cardRate * 100;

    // Distribuir os centavos de resto na 1ª parcela para garantir soma exata
    const totalCents = Math.round(entryAmount * 100);
    const baseCents = Math.floor(totalCents / installmentsCount);
    const remainderCents = totalCents - (baseCents * installmentsCount);

    const generated = [];

    for (let i = 0; i < installmentsCount; i++) {
      const cents = baseCents + (i === 0 ? remainderCents : 0);
      const originalValue = cents / 100;
      let finalValue = originalValue;

      if (ratePaidBy === 'cliente' && cardRate > 0) {
        finalValue = Math.round(originalValue * (1 + cardRate) * 100) / 100;
      }

      generated.push({
        installment: i + 1,
        originalValue: originalValue,
        ratePercentage: ratePercentage,
        rateAmount: ratePaidBy === 'cliente' ? Math.round((finalValue - originalValue) * 100) / 100 : 0,
        finalValue: finalValue,
        date: '',
      });
    }

    setGeneratedEntryCardInstallments(generated);

    const splitsWithDates = generated.map((g, i) => {
      let date;
      if (formData.entry_date) {
        // Usar entry_date como base para a primeira parcela
        const [year, month, day] = formData.entry_date.split('-').map(Number);
        date = new Date(year, month - 1, day);
        date.setMonth(date.getMonth() + i);
      } else {
        // Fallback para hoje
        date = new Date();
        date.setMonth(date.getMonth() + i);
      }
      // When client pays tax, entry_splits stores original value (without tax)
      return {
        amount: ratePaidBy === 'cliente' ? g.originalValue : g.finalValue,
        date: date.toISOString().split('T')[0],
        payment_type: 'card',
      };
    });

    setFormData(prev => ({
      ...prev,
      entry_splits: splitsWithDates,
    }));

    toast.success('Parcelas de entrada geradas com sucesso!');
  };

  const generateInstallmentCardInstallments = () => {
    const remainingAmount = calculateRemainingAmount();
    const installmentsCount = parseInt(formData.installments) || 1;
    if (installmentsCount <= 0 || remainingAmount <= 0) return;

    const cardRate = getCardRate(installmentCardType, installmentsCount);
    const ratePercentage = cardRate * 100;
    const whoPays = ratePaidBy;

    // Distribuir os centavos de resto na 1ª parcela para que a soma dos valores originais seja EXATAMENTE igual a remainingAmount
    const totalCents = Math.round(remainingAmount * 100);
    const baseCents = Math.floor(totalCents / installmentsCount);
    const remainderCents = totalCents - (baseCents * installmentsCount);

    const generated = [];

    for (let i = 0; i < installmentsCount; i++) {
      const cents = baseCents + (i === 0 ? remainderCents : 0);
      const originalValue = cents / 100;
      let finalValue = originalValue;

      if (whoPays === 'cliente' && cardRate > 0) {
        finalValue = Math.round(originalValue * (1 + cardRate) * 100) / 100;
      }

      generated.push({
        installment: i + 1,
        originalValue: originalValue,
        ratePercentage: ratePercentage,
        rateAmount: whoPays === 'cliente' ? Math.round((finalValue - originalValue) * 100) / 100 : 0,
        finalValue: finalValue,
        date: '',
      });
    }

    setGeneratedInstallmentCardInstallments(generated);

    // Update installmentSplits with the generated values
    const splitsWithDates = generated.map((g, i) => {
      let date;
      if (formData.first_installment_date) {
        const [year, month, day] = formData.first_installment_date.split('-').map(Number);
        date = new Date(year, month - 1, day);
        date.setMonth(date.getMonth() + i);
      } else {
        date = new Date();
        date.setMonth(date.getMonth() + i);
      }
      // When client pays tax, installmentSplits stores original value (without tax)
      return {
        amount: whoPays === 'cliente' ? g.originalValue : g.finalValue,
        date: date.toISOString().split('T')[0],
        payment_type: 'card',
      };
    });

    setInstallmentSplits(splitsWithDates);

    toast.success('Parcelas geradas com sucesso!');
  };

  // Ajustar valor final manualmente de uma parcela de cartão (Saldo Restante)
  const handleInstallmentCardFinalValueChange = (index, val) => {
    const rawVal = typeof val === 'number' ? val : parseFormattedCurrency(val);
    const remainingAmount = calculateRemainingAmount();
    const installmentsCount = parseInt(formData.installments) || 1;
    const cardRate = getCardRate(installmentCardType, installmentsCount);
    const whoPays = ratePaidBy;

    const expectedTotalFinal = whoPays === 'cliente' && cardRate > 0
      ? Math.round(remainingAmount * (1 + cardRate) * 100) / 100
      : remainingAmount;

    // Regra: Uma parcela individual não pode ultrapassar o valor total
    let safeVal = Math.max(0, rawVal);
    if (safeVal > expectedTotalFinal) {
      toast.error(`O valor da parcela não pode extrapolar o total de ${formatCurrency(expectedTotalFinal)}`);
      safeVal = expectedTotalFinal;
    }

    const updated = [...generatedInstallmentCardInstallments];
    let origVal = safeVal;
    let rateAmt = 0;

    if (whoPays === 'cliente' && cardRate > 0) {
      origVal = Math.round((safeVal / (1 + cardRate)) * 100) / 100;
      rateAmt = Math.round((safeVal - origVal) * 100) / 100;
    }

    updated[index] = {
      ...updated[index],
      finalValue: safeVal,
      originalValue: origVal,
      rateAmount: rateAmt,
    };

    setGeneratedInstallmentCardInstallments(updated);

    // Sincronizar com installmentSplits
    const updatedSplits = updated.map((g, i) => {
      let date = installmentSplits[i]?.date;
      if (!date) {
        if (formData.first_installment_date) {
          const [year, month, day] = formData.first_installment_date.split('-').map(Number);
          const d = new Date(year, month - 1, day);
          d.setMonth(d.getMonth() + i);
          date = d.toISOString().split('T')[0];
        } else {
          const d = new Date();
          d.setMonth(d.getMonth() + i);
          date = d.toISOString().split('T')[0];
        }
      }
      return {
        amount: whoPays === 'cliente' ? g.originalValue : g.finalValue,
        date: date,
        payment_type: 'card',
      };
    });

    setInstallmentSplits(updatedSplits);
  };

  // Ajustar automaticamente a diferença na última parcela para fechar 100% no total (Cartão de Parcelas)
  const handleAdjustInstallmentCardDifference = () => {
    if (!generatedInstallmentCardInstallments || generatedInstallmentCardInstallments.length <= 1) return;

    const remainingAmount = calculateRemainingAmount();
    const installmentsCount = parseInt(formData.installments) || 1;
    const cardRate = getCardRate(installmentCardType, installmentsCount);
    const whoPays = ratePaidBy;

    const expectedTotalFinal = whoPays === 'cliente' && cardRate > 0
      ? Math.round(remainingAmount * (1 + cardRate) * 100) / 100
      : remainingAmount;

    const lastIndex = generatedInstallmentCardInstallments.length - 1;
    const sumOthers = generatedInstallmentCardInstallments.slice(0, lastIndex).reduce((sum, inst) => sum + (parseFloat(inst.finalValue) || 0), 0);
    const diffFinal = Math.round((expectedTotalFinal - sumOthers) * 100) / 100;

    if (diffFinal < 0) {
      toast.error(`A soma das primeiras parcelas (${formatCurrency(sumOthers)}) já extrapolou o total (${formatCurrency(expectedTotalFinal)})!`);
      return;
    }

    handleInstallmentCardFinalValueChange(lastIndex, diffFinal);
    toast.success('Diferença ajustada na última parcela com sucesso!');
  };

  // Ajustar valor final manualmente de uma parcela de cartão (Entrada)
  const handleEntryCardFinalValueChange = (index, val) => {
    const rawVal = typeof val === 'number' ? val : parseFormattedCurrency(val);
    const entryAmount = formData.has_entry ? parseFormattedCurrency(formData.entry_amount) : 0;
    const splitsCount = parseInt(formData.entry_splits_count) || 1;
    const cardRate = getCardRate(entryCardType, splitsCount);
    const whoPays = ratePaidBy;

    const expectedTotalFinal = whoPays === 'cliente' && cardRate > 0
      ? Math.round(entryAmount * (1 + cardRate) * 100) / 100
      : entryAmount;

    let safeVal = Math.max(0, rawVal);
    if (safeVal > expectedTotalFinal) {
      toast.error(`O valor da parcela não pode extrapolar o total de ${formatCurrency(expectedTotalFinal)}`);
      safeVal = expectedTotalFinal;
    }

    const updated = [...generatedEntryCardInstallments];
    let origVal = safeVal;
    let rateAmt = 0;

    if (whoPays === 'cliente' && cardRate > 0) {
      origVal = Math.round((safeVal / (1 + cardRate)) * 100) / 100;
      rateAmt = Math.round((safeVal - origVal) * 100) / 100;
    }

    updated[index] = {
      ...updated[index],
      finalValue: safeVal,
      originalValue: origVal,
      rateAmount: rateAmt,
    };

    setGeneratedEntryCardInstallments(updated);

    const updatedSplits = updated.map((g, i) => {
      let date = formData.entry_splits?.[i]?.date;
      if (!date) {
        if (formData.entry_date) {
          const [year, month, day] = formData.entry_date.split('-').map(Number);
          const d = new Date(year, month - 1, day);
          d.setMonth(d.getMonth() + i);
          date = d.toISOString().split('T')[0];
        } else {
          const d = new Date();
          d.setMonth(d.getMonth() + i);
          date = d.toISOString().split('T')[0];
        }
      }
      return {
        amount: whoPays === 'cliente' ? g.originalValue : g.finalValue,
        date: date,
        payment_type: 'card',
      };
    });

    setFormData(prev => ({
      ...prev,
      entry_splits: updatedSplits
    }));
  };

  // Ajustar automaticamente a diferença na última parcela para fechar 100% no total (Cartão de Entrada)
  const handleAdjustEntryCardDifference = () => {
    if (!generatedEntryCardInstallments || generatedEntryCardInstallments.length <= 1) return;

    const entryAmount = formData.has_entry ? parseFormattedCurrency(formData.entry_amount) : 0;
    const splitsCount = parseInt(formData.entry_splits_count) || 1;
    const cardRate = getCardRate(entryCardType, splitsCount);
    const whoPays = ratePaidBy;

    const expectedTotalFinal = whoPays === 'cliente' && cardRate > 0
      ? Math.round(entryAmount * (1 + cardRate) * 100) / 100
      : entryAmount;

    const lastIndex = generatedEntryCardInstallments.length - 1;
    const sumOthers = generatedEntryCardInstallments.slice(0, lastIndex).reduce((sum, inst) => sum + (parseFloat(inst.finalValue) || 0), 0);
    const diffFinal = Math.round((expectedTotalFinal - sumOthers) * 100) / 100;

    if (diffFinal < 0) {
      toast.error(`A soma das primeiras parcelas (${formatCurrency(sumOthers)}) já extrapolou o total (${formatCurrency(expectedTotalFinal)})!`);
      return;
    }

    handleEntryCardFinalValueChange(lastIndex, diffFinal);
    toast.success('Diferença ajustada na última parcela com sucesso!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle date strings that may include time component
    const dateOnly = dateStr.split('T')[0];
    if (dateOnly.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateOnly.split('-');
      return `${day}/${month}/${year}`;
    }
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const getPaymentMethodLabel = (method) => {
    if (!method) return '-';
    const m = String(method).toLowerCase().trim();
    const labels = {
      'cash': 'Dinheiro',
      'dinheiro': 'Dinheiro',
      'pix': 'PIX',
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'card': 'Cartão',
      'cartao': 'Cartão',
      'cartão': 'Cartão',
      'bank_transfer': 'Transferência Bancária',
      'transferencia': 'Transferência Bancária',
      'cheque': 'Cheque',
      'cheque_direct': 'Cheque direto ao fornecedor',
      'pix_direct': 'PIX direto ao fornecedor',
      'boleto': 'Boleto',
      'cash_installments': 'Dinheiro Parcelado',
    };
    return labels[m] || method;
  };

  // Verificar se há parcelas já pagas (campos bloqueados na edição)
  const hasPaidInstallments = editingBalance && editingBalance.installments_paid > 0;
  const hasPaidEntry = editingBalance && editingBalance.entry_paid;

  // Renderização do passo atual do wizard
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            {/* Aviso de campos bloqueados na edição */}
            {editingBalance && (hasPaidInstallments || hasPaidEntry) && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Edição com restrições</p>
                    <p className="text-sm text-amber-700 mt-1">
                      {hasPaidEntry && '• A entrada já foi recebida e não pode ser alterada. '}
                      {hasPaidInstallments && `• ${editingBalance.installments_paid} parcela(s) já foi(ram) paga(s) e não pode(m) ser alterada(s).`}
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      Você pode remanejar apenas as <strong>{editingBalance.installments - editingBalance.installments_paid} parcela(s) restante(s)</strong>.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Valor Total */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Total (R$) *
                </label>
                <CurrencyInput
                  name="total_amount"
                  value={formData.total_amount}
                  onChange={(value) => setFormData(prev => ({ ...prev, total_amount: value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="59000"
                />
              </div>

              {/* Data de Referência */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Referência *
                </label>
                <input
                  type="date"
                  name="reference_date"
                  value={formData.reference_date}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">Data retroativa para o financeiro</p>
              </div>
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Ex: Projeto de móveis planejados - José Silva"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); handleNextStep(e); }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Próximo
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            {/* Teve Entrada? - Toggle/Checkbox */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                name="has_entry"
                id="has_entry"
                checked={formData.has_entry}
                onChange={handleChange}
                disabled={hasPaidEntry}
                className="w-5 h-5 text-green-600 rounded focus:ring-green-500 disabled:opacity-50"
              />
              <label htmlFor="has_entry" className={`text-sm font-medium ${hasPaidEntry ? 'text-gray-400' : 'text-gray-700'}`}>
                Teve Entrada? / Nova Entrada? {hasPaidEntry && '(bloqueado - já recebido)'}
              </label>
            </div>

            {formData.has_entry ? (
              <div className="space-y-4">
                {/* Seletor de número de parcelas da entrada - SÓ MOSTRA SE NÃO FOR CARTÃO */}
                {formData.entry_payment_type !== 'card' && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-4">
                      <label className="text-sm font-medium text-gray-700">
                        Fracionar entrada em:
                      </label>
                      <select
                        name="entry_splits_count"
                        value={formData.entry_splits_count}
                        onChange={handleChange}
                        disabled={hasPaidEntry}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="1">1 parcela</option>
                        <option value="2">2 parcelas</option>
                        <option value="3">3 parcelas</option>
                        <option value="4">4 parcelas</option>
                        <option value="5">5 parcelas</option>
                        <option value="6">6 parcelas</option>
                        <option value="7">7 parcelas</option>
                        <option value="8">8 parcelas</option>
                        <option value="9">9 parcelas</option>
                        <option value="10">10 parcelas</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Campos de splits ou campo único */}
                {parseInt(formData.entry_splits_count) > 1 && formData.entry_payment_type !== 'card' ? (
                  <div className="space-y-3">
                    {formData.entry_splits.map((split, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="md:col-span-1">
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            Parcela {index + 1} - Valor (R$) *
                          </label>
                          <CurrencyInput
                            value={split.amount}
                            onChange={(val) => {
                              setFormData(prev => {
                                const newSplits = [...prev.entry_splits];
                                newSplits[index] = { ...newSplits[index], amount: val };
                                return { ...prev, entry_splits: newSplits };
                              });
                            }}
                            disabled={hasPaidEntry}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                            placeholder="0,00"
                          />
                        </div>
                        <div className="md:col-span-1">
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            Parcela {index + 1} - Data *
                          </label>
                          <input
                            type="date"
                            value={split.date}
                            onChange={(e) => handleSplitChange(index, 'date', e.target.value)}
                            disabled={hasPaidEntry}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                          />
                        </div>
                        <div className="md:col-span-1">
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            Parcela {index + 1} - Forma Pgto
                          </label>
                          <select
                            value={split.payment_type}
                            onChange={(e) => handleSplitChange(index, 'payment_type', e.target.value)}
                            disabled={hasPaidEntry}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                          >
                            {formData.entry_payment_type === 'card' ? (
                              <>
                                <option value="card">Cartão</option>
                              </>
                            ) : (
                              <>
                                <option value="cash">Dinheiro</option>
                                <option value="pix">PIX</option>
                                <option value="credit_card">Cartão de Crédito</option>
                                <option value="debit_card">Cartão de Débito</option>
                                <option value="bank_transfer">Transferência Bancária</option>
                                <option value="cheque">Cheque</option>
                                <option value="boleto">Boleto</option>
                              </>
                            )}
                          </select>
                        </div>
                        <div className="md:col-span-1 flex items-end">
                          <div className="text-xs text-gray-500 pb-2">
                            {split.amount && split.date ? (
                              <span className="text-green-600 font-medium">✓ Pronto</span>
                            ) : (
                              <span className="text-amber-600">Pendente</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {(() => {
                      const splitsTotal = formData.entry_splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                      const projectTotal = parseFloat(formData.total_amount) || 0;
                      const exceedsTotal = splitsTotal > projectTotal;
                      return (
                        <div className={`flex justify-between items-center p-3 rounded-lg border ${exceedsTotal ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                          <span className="text-sm font-medium text-gray-700">Total das parcelas:</span>
                          <span className={`text-sm font-bold ${exceedsTotal ? 'text-red-700' : 'text-green-700'}`}>
                            {formatCurrency(splitsTotal)}
                            {exceedsTotal && ` (excede total de ${formatCurrency(projectTotal)})`}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valor da Entrada (R$) *
                      </label>
                      <CurrencyInput
                        name="entry_amount"
                        value={formData.entry_amount}
                        onChange={(value) => setFormData(prev => ({ ...prev, entry_amount: value }))}
                        required={formData.has_entry}
                        disabled={hasPaidEntry}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="5000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Forma de Pagamento
                      </label>
                      <select
                        name="entry_payment_type"
                        value={formData.entry_payment_type}
                        onChange={handleChange}
                        disabled={hasPaidEntry}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="cash">Dinheiro</option>
                        <option value="pix">PIX</option>
                        <option value="pix_direct">PIX direto ao fornecedor</option>
                        <option value="card">Cartão</option>
                        <option value="bank_transfer">Transferência Bancária</option>
                        <option value="cheque">Cheque</option>
                        <option value="cheque_direct">Cheque direto ao fornecedor</option>
                        <option value="boleto">Boleto</option>
                      </select>
                    </div>
                    {formData.entry_payment_type === 'card' && (
                      <div className="md:col-span-3 p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <label className="text-sm font-medium text-gray-700">É Crédito ou Débito?</label>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                              <input type="radio" name="entryCardType" value="CREDIT" checked={entryCardType === 'CREDIT'} onChange={(e) => setEntryCardType(e.target.value)} className="w-4 h-4" />
                              <span className="text-sm">Crédito</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                              <input type="radio" name="entryCardType" value="DEBIT" checked={entryCardType === 'DEBIT'} onChange={(e) => setEntryCardType(e.target.value)} className="w-4 h-4" />
                              <span className="text-sm">Débito</span>
                            </label>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <label className="text-sm font-medium text-gray-700">É à vista ou parcelado?</label>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                              <input type="radio" name="entryIsParcelado" value={false} checked={!entryIsParcelado} onChange={(e) => setEntryIsParcelado(false)} className="w-4 h-4" />
                              <span className="text-sm">À vista</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                              <input type="radio" name="entryIsParcelado" value={true} checked={entryIsParcelado} onChange={(e) => setEntryIsParcelado(true)} className="w-4 h-4" />
                              <span className="text-sm">Parcelado</span>
                            </label>
                          </div>
                        </div>
                        {entryIsParcelado && (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                            <label className="text-sm font-medium text-gray-700">Quantidade de parcelas:</label>
                            <input
                              type="number"
                              name="entry_splits_count"
                              value={formData.entry_splits_count}
                              onChange={handleChange}
                              min="2"
                              max="12"
                              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        )}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <label className="text-sm font-medium text-gray-700">Quem paga a taxa?</label>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                              <input type="radio" name="entryRatePaidBy" value="empresa" checked={ratePaidBy === 'empresa'} onChange={(e) => setRatePaidBy(e.target.value)} className="w-4 h-4" />
                              <span className="text-sm">Empresa (sem juros)</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                              <input type="radio" name="entryRatePaidBy" value="cliente" checked={ratePaidBy === 'cliente'} onChange={(e) => setRatePaidBy(e.target.value)} className="w-4 h-4" />
                              <span className="text-sm">Cliente (com taxa)</span>
                            </label>
                          </div>
                        </div>

                        {/* Botão Gerar Parcelas */}
                        <button
                          type="button"
                          onClick={generateEntryCardInstallments}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                            <path d="M21 3v5h-5"></path>
                            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                            <path d="M8 16H3v5"></path>
                          </svg>
                          Gerar Parcelas
                        </button>

                        {/* Tabela de Parcelas Geradas */}
                        {generatedEntryCardInstallments.length > 0 && (
                          <div className="mt-4">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <h5 className="text-sm font-medium text-gray-700">
                                Parcelas Geradas da Entrada (edite os valores finais se desejar):
                              </h5>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={generateEntryCardInstallments}
                                  className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded flex items-center gap-1 transition-colors border border-gray-300 font-medium"
                                  title="Restaurar divisão igualitária"
                                >
                                  <RefreshCw className="w-3.5 h-3.5" />
                                  Dividir Igualmente
                                </button>
                                <button
                                  type="button"
                                  onClick={handleAdjustEntryCardDifference}
                                  className="px-2.5 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded flex items-center gap-1 transition-colors border border-purple-300 font-medium"
                                  title="Ajustar diferença na última parcela para bater o total"
                                >
                                  Ajustar na Última
                                </button>
                              </div>
                            </div>
                            <div className="overflow-x-auto">
                            <table className="w-full text-sm bg-white rounded-lg overflow-hidden min-w-[500px]">
                              <thead className="bg-purple-100">
                                <tr>
                                  <th className="px-3 py-2 text-left">#</th>
                                  <th className="px-3 py-2 text-right">Valor Original</th>
                                  <th className="px-3 py-2 text-right">Taxa (%)</th>
                                  <th className="px-3 py-2 text-right">Valor Taxa</th>
                                  <th className="px-3 py-2 text-right">Valor Final</th>
                                </tr>
                              </thead>
                              <tbody>
                                {generatedEntryCardInstallments.map((inst, index) => (
                                  <tr key={inst.installment} className="border-t hover:bg-gray-50/50">
                                    <td className="px-3 py-2 font-medium">{inst.installment}ª</td>
                                    <td className="px-3 py-2 text-right">{formatCurrency(inst.originalValue)}</td>
                                    <td className="px-3 py-2 text-right">{inst.ratePercentage.toFixed(2)}%</td>
                                    <td className="px-3 py-2 text-right text-red-600">{formatCurrency(inst.rateAmount)}</td>
                                    <td className="px-3 py-2 text-right font-medium text-green-600">
                                      <div className="flex justify-end">
                                        <CurrencyInput
                                          id={`entry_card_final_${inst.installment}`}
                                          value={inst.finalValue}
                                          onChange={(val) => handleEntryCardFinalValueChange(index, val)}
                                          className="w-28 sm:w-32 px-2 py-1 text-right text-xs sm:text-sm font-semibold text-green-700 bg-white border border-green-300 rounded focus:ring-2 focus:ring-green-500 shadow-sm"
                                          placeholder="0,00"
                                        />
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50 font-medium">
                                <tr>
                                  <td className="px-3 py-2">Total</td>
                                  <td className="px-3 py-2 text-right">{formatCurrency(generatedEntryCardInstallments.reduce((sum, i) => sum + (parseFloat(i.originalValue) || 0), 0))}</td>
                                  <td className="px-3 py-2 text-right"></td>
                                  <td className="px-3 py-2 text-right text-red-600">{formatCurrency(generatedEntryCardInstallments.reduce((sum, i) => sum + (parseFloat(i.rateAmount) || 0), 0))}</td>
                                  <td className="px-3 py-2 text-right font-bold">
                                    {(() => {
                                      const currentFinalSum = Math.round(generatedEntryCardInstallments.reduce((sum, i) => sum + (parseFloat(i.finalValue) || 0), 0) * 100) / 100;
                                      const entryAmount = formData.has_entry ? parseFormattedCurrency(formData.entry_amount) : 0;
                                      const splitsCount = parseInt(formData.entry_splits_count) || 1;
                                      const cardRate = getCardRate(entryCardType, splitsCount);
                                      const expectedTotalFinal = ratePaidBy === 'cliente' && cardRate > 0
                                        ? Math.round(entryAmount * (1 + cardRate) * 100) / 100
                                        : entryAmount;
                                      const diff = Math.round(Math.abs(currentFinalSum - expectedTotalFinal) * 100) / 100;

                                      if (diff <= 0.01) {
                                        return <span className="text-green-600">{formatCurrency(currentFinalSum)} ✓</span>;
                                      }
                                      if (currentFinalSum > expectedTotalFinal) {
                                        return <span className="text-red-600" title={`Extrapolou em ${formatCurrency(currentFinalSum - expectedTotalFinal)}`}>{formatCurrency(currentFinalSum)} ⚠️</span>;
                                      }
                                      return <span className="text-amber-600" title={`Faltam ${formatCurrency(expectedTotalFinal - currentFinalSum)}`}>{formatCurrency(currentFinalSum)} ⚠️</span>;
                                    })()}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                            </div>

                            {/* Feedback sobre a soma do Valor Final das Parcelas da Entrada */}
                            {(() => {
                              const currentFinalSum = Math.round(generatedEntryCardInstallments.reduce((sum, i) => sum + (parseFloat(i.finalValue) || 0), 0) * 100) / 100;
                              const entryAmount = formData.has_entry ? parseFormattedCurrency(formData.entry_amount) : 0;
                              const splitsCount = parseInt(formData.entry_splits_count) || 1;
                              const cardRate = getCardRate(entryCardType, splitsCount);
                              const expectedTotalFinal = ratePaidBy === 'cliente' && cardRate > 0
                                ? Math.round(entryAmount * (1 + cardRate) * 100) / 100
                                : entryAmount;
                              const diff = Math.round((currentFinalSum - expectedTotalFinal) * 100) / 100;

                              if (Math.abs(diff) > 0.01) {
                                return (
                                  <div className={`mt-2.5 p-2.5 rounded-lg border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs ${
                                    diff > 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'
                                  }`}>
                                    <div className="flex items-center gap-1.5">
                                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                      <span>
                                        {diff > 0
                                          ? `A soma das parcelas de entrada (${formatCurrency(currentFinalSum)}) extrapolou o total (${formatCurrency(expectedTotalFinal)}) em ${formatCurrency(diff)}!`
                                          : `A soma das parcelas de entrada (${formatCurrency(currentFinalSum)}) está abaixo do total (${formatCurrency(expectedTotalFinal)}). Faltam ${formatCurrency(Math.abs(diff))}.`}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={handleAdjustEntryCardDifference}
                                      className="px-2.5 py-1 bg-white hover:bg-gray-50 border rounded font-medium shadow-sm whitespace-nowrap self-end sm:self-auto"
                                    >
                                      Ajustar na última parcela
                                    </button>
                                  </div>
                                );
                              }

                              return (
                                <p className="text-xs text-green-600 mt-2 font-medium flex items-center gap-1">
                                  <Check className="w-3.5 h-3.5" /> Total conferido: {formatCurrency(expectedTotalFinal)} (100% respeitado)
                                </p>
                              );
                            })()}

                            {ratePaidBy === 'empresa' && (
                              <p className="text-xs text-purple-600 mt-2">
                                * Taxa de {(getCardRate(entryCardType, parseInt(formData.entry_splits_count) || 1) * 100).toFixed(2)}% será absorvida pela empresa
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Recebimento *
                      </label>
                      <input
                        type="date"
                        name="entry_date"
                        value={formData.entry_date}
                        onChange={handleChange}
                        required={formData.has_entry}
                        disabled={hasPaidEntry}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        title={hasPaidEntry ? 'Entrada já foi recebida e não pode ser alterada' : ''}
                      />
                    </div>
                    {formData.entry_payment_type && formData.entry_payment_type.endsWith('_direct') && (
                      <div className="md:col-span-3 mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fornecedor (pagamento direto) *
                        </label>
                        <select
                          name="entry_supplier_id"
                          value={formData.entry_supplier_id || ''}
                          onChange={handleChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Selecione o fornecedor</option>
                          {suppliers.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-500">Sem entrada</p>
              </div>
            )}

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handlePrevStep}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <ChevronLeft className="w-4 h-4" />
                Voltar
              </button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); handleNextStep(e); }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Próximo
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );

      case 3:
        const remainingAmount = calculateRemainingAmount();
        const installmentAmount = calculateInstallmentAmount();
        const remainder = calculateRemainder();

        return (
          <div className="space-y-4">
            {/* Valor Restante */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">
                  Valor Restante: {formatCurrency(remainingAmount)}
                </span>
              </div>
              {formData.installments > 1 && (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <p className="text-sm text-green-700">
                    Divisão: {formData.installments}x de {formatCurrency(installmentAmount)}
                    {remainder > 0 && (
                      <span className="font-medium">
                        {" "}(1ª parcela: {formatCurrency(installmentAmount + remainder)} com diferença de {formatCurrency(remainder)})
                      </span>
                    )}
                  </p>
                  {remainder > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      A primeira parcela recebe {formatCurrency(remainder)} de diferença para compensar a divisão
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Quantidade de Parcelas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade de Parcelas * {hasPaidInstallments && `(mínimo: ${editingBalance?.installments_paid || 0})`}
                </label>
                <input
                  type="number"
                  name="installments"
                  value={formData.installments}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  min={hasPaidInstallments ? (editingBalance?.installments_paid || 0) : 1}
                  max="24"
                  placeholder="10"
                />
                {hasPaidInstallments && (
                  <p className="text-xs text-amber-600 mt-1">
                    {editingBalance.installments_paid} parcela(s) já paga(s) - não é possível reduzir abaixo disso
                  </p>
                )}
              </div>

              {/* Forma de Pagamento das Parcelas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forma de Pagamento das Parcelas
                </label>
                <select
                  name="payment_type"
                  value={formData.payment_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="cash">Dinheiro</option>
                  <option value="pix">PIX</option>
                  <option value="card">Cartão</option>
                  <option value="bank_transfer">Transferência Bancária</option>
                  <option value="cheque">Cheque</option>
                  <option value="boleto">Boleto</option>
                  <option value="cheque_installments">Cheque Parcelado</option>
                  <option value="cash_installments">Dinheiro Parcelado</option>
                </select>
              </div>
              {formData.payment_type === 'card' && (
                <div className="md:col-span-2 p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <label className="text-sm font-medium text-gray-700">É Crédito ou Débito?</label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                        <input type="radio" name="installmentCardType" value="CREDIT" checked={installmentCardType === 'CREDIT'} onChange={(e) => setInstallmentCardType(e.target.value)} className="w-4 h-4" />
                        <span className="text-sm">Crédito</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                        <input type="radio" name="installmentCardType" value="DEBIT" checked={installmentCardType === 'DEBIT'} onChange={(e) => setInstallmentCardType(e.target.value)} className="w-4 h-4" />
                        <span className="text-sm">Débito</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <label className="text-sm font-medium text-gray-700">É à vista ou parcelado?</label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                        <input type="radio" name="installmentIsParcelado" value={false} checked={!installmentIsParcelado} onChange={(e) => setInstallmentIsParcelado(false)} className="w-4 h-4" />
                        <span className="text-sm">À vista</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                        <input type="radio" name="installmentIsParcelado" value={true} checked={installmentIsParcelado} onChange={(e) => setInstallmentIsParcelado(true)} className="w-4 h-4" />
                        <span className="text-sm">Parcelado</span>
                      </label>
                    </div>
                  </div>
                  {installmentIsParcelado && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <label className="text-sm font-medium text-gray-700">Quantidade de parcelas:</label>
                      <input
                        type="number"
                        name="installments"
                        value={formData.installments}
                        onChange={handleChange}
                        min="2"
                        max="12"
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <label className="text-sm font-medium text-gray-700">Quem paga a taxa?</label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                        <input type="radio" name="installmentRatePaidBy" value="empresa" checked={ratePaidBy === 'empresa'} onChange={(e) => setRatePaidBy(e.target.value)} className="w-4 h-4" />
                        <span className="text-sm">Empresa (sem juros)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                        <input type="radio" name="installmentRatePaidBy" value="cliente" checked={ratePaidBy === 'cliente'} onChange={(e) => setRatePaidBy(e.target.value)} className="w-4 h-4" />
                        <span className="text-sm">Cliente (com taxa)</span>
                      </label>
                    </div>
                    {ratePaidBy === 'cliente' && (
                      <span className="text-xs text-purple-600">
                        Taxa de {(getCardRate(installmentCardType, installmentIsParcelado ? formData.installments : 1) * 100).toFixed(2)}% será incluída
                      </span>
                    )}
                  </div>

                  {/* Botão Gerar Parcelas */}
                  <button
                    type="button"
                    onClick={generateInstallmentCardInstallments}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                      <path d="M21 3v5h-5"></path>
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                      <path d="M8 16H3v5"></path>
                    </svg>
                    Gerar Parcelas
                  </button>

                  {/* Tabela de Parcelas Geradas */}
                  {generatedInstallmentCardInstallments.length > 0 && (
                    <div className="mt-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <h5 className="text-sm font-medium text-gray-700">
                          Parcelas Geradas (edite os valores finais se desejar):
                        </h5>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={generateInstallmentCardInstallments}
                            className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded flex items-center gap-1 transition-colors border border-gray-300 font-medium"
                            title="Restaurar divisão igualitária"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Dividir Igualmente
                          </button>
                          <button
                            type="button"
                            onClick={handleAdjustInstallmentCardDifference}
                            className="px-2.5 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded flex items-center gap-1 transition-colors border border-purple-300 font-medium"
                            title="Ajustar diferença na última parcela para bater o total"
                          >
                            Ajustar na Última
                          </button>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                      <table className="w-full text-sm bg-white rounded-lg overflow-hidden min-w-[500px]">
                        <thead className="bg-purple-100">
                          <tr>
                            <th className="px-3 py-2 text-left">#</th>
                            <th className="px-3 py-2 text-right">Valor Original</th>
                            <th className="px-3 py-2 text-right">Taxa (%)</th>
                            <th className="px-3 py-2 text-right">Valor Taxa</th>
                            <th className="px-3 py-2 text-right">Valor Final</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generatedInstallmentCardInstallments.map((inst, index) => (
                            <tr key={inst.installment} className="border-t hover:bg-gray-50/50">
                              <td className="px-3 py-2 font-medium">{inst.installment}ª</td>
                              <td className="px-3 py-2 text-right">{formatCurrency(inst.originalValue)}</td>
                              <td className="px-3 py-2 text-right">{inst.ratePercentage.toFixed(2)}%</td>
                              <td className="px-3 py-2 text-right text-red-600">{formatCurrency(inst.rateAmount)}</td>
                              <td className="px-3 py-2 text-right font-medium text-green-600">
                                <div className="flex justify-end">
                                  <CurrencyInput
                                    id={`installment_card_final_${inst.installment}`}
                                    value={inst.finalValue}
                                    onChange={(val) => handleInstallmentCardFinalValueChange(index, val)}
                                    className="w-28 sm:w-32 px-2 py-1 text-right text-xs sm:text-sm font-semibold text-green-700 bg-white border border-green-300 rounded focus:ring-2 focus:ring-green-500 shadow-sm"
                                    placeholder="0,00"
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50 font-medium">
                          <tr>
                            <td className="px-3 py-2">Total</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(generatedInstallmentCardInstallments.reduce((sum, i) => sum + (parseFloat(i.originalValue) || 0), 0))}</td>
                            <td className="px-3 py-2 text-right"></td>
                            <td className="px-3 py-2 text-right text-red-600">{formatCurrency(generatedInstallmentCardInstallments.reduce((sum, i) => sum + (parseFloat(i.rateAmount) || 0), 0))}</td>
                            <td className="px-3 py-2 text-right font-bold">
                              {(() => {
                                const currentFinalSum = Math.round(generatedInstallmentCardInstallments.reduce((sum, i) => sum + (parseFloat(i.finalValue) || 0), 0) * 100) / 100;
                                const remaining = calculateRemainingAmount();
                                const cardRate = getCardRate(installmentCardType, parseInt(formData.installments) || 1);
                                const expectedTotalFinal = ratePaidBy === 'cliente' && cardRate > 0
                                  ? Math.round(remaining * (1 + cardRate) * 100) / 100
                                  : remaining;
                                const diff = Math.round(Math.abs(currentFinalSum - expectedTotalFinal) * 100) / 100;

                                if (diff <= 0.01) {
                                  return <span className="text-green-600">{formatCurrency(currentFinalSum)} ✓</span>;
                                }
                                if (currentFinalSum > expectedTotalFinal) {
                                  return <span className="text-red-600" title={`Extrapolou em ${formatCurrency(currentFinalSum - expectedTotalFinal)}`}>{formatCurrency(currentFinalSum)} ⚠️</span>;
                                }
                                return <span className="text-amber-600" title={`Faltam ${formatCurrency(expectedTotalFinal - currentFinalSum)}`}>{formatCurrency(currentFinalSum)} ⚠️</span>;
                              })()}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                      </div>

                      {/* Feedback sobre a soma do Valor Final das Parcelas */}
                      {(() => {
                        const currentFinalSum = Math.round(generatedInstallmentCardInstallments.reduce((sum, i) => sum + (parseFloat(i.finalValue) || 0), 0) * 100) / 100;
                        const remaining = calculateRemainingAmount();
                        const cardRate = getCardRate(installmentCardType, parseInt(formData.installments) || 1);
                        const expectedTotalFinal = ratePaidBy === 'cliente' && cardRate > 0
                          ? Math.round(remaining * (1 + cardRate) * 100) / 100
                          : remaining;
                        const diff = Math.round((currentFinalSum - expectedTotalFinal) * 100) / 100;

                        if (Math.abs(diff) > 0.01) {
                          return (
                            <div className={`mt-2.5 p-2.5 rounded-lg border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs ${
                              diff > 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'
                            }`}>
                              <div className="flex items-center gap-1.5">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span>
                                  {diff > 0
                                    ? `A soma das parcelas (${formatCurrency(currentFinalSum)}) extrapolou o total (${formatCurrency(expectedTotalFinal)}) em ${formatCurrency(diff)}!`
                                    : `A soma das parcelas (${formatCurrency(currentFinalSum)}) está abaixo do total (${formatCurrency(expectedTotalFinal)}). Faltam ${formatCurrency(Math.abs(diff))}.`}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={handleAdjustInstallmentCardDifference}
                                className="px-2.5 py-1 bg-white hover:bg-gray-50 border rounded font-medium shadow-sm whitespace-nowrap self-end sm:self-auto"
                              >
                                Ajustar na última parcela
                              </button>
                            </div>
                          );
                        }

                        return (
                          <p className="text-xs text-green-600 mt-2 font-medium flex items-center gap-1">
                            <Check className="w-3.5 h-3.5" /> Total conferido: {formatCurrency(expectedTotalFinal)} (100% respeitado)
                          </p>
                        );
                      })()}

                      {ratePaidBy === 'empresa' && (
                        <p className="text-xs text-purple-600 mt-2">
                          * Taxa de {(getCardRate(installmentCardType, parseInt(formData.installments) || 1) * 100).toFixed(2)}% será absorvida pela empresa
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Data da 1ª Parcela */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data da 1ª Parcela
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    name="first_installment_date"
                    value={formData.first_installment_date}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateInstallmentDates}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
                    title="Gerar datas das parcelas (30 dias entre cada)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                      <path d="M21 3v5h-5"></path>
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                      <path d="M8 16H3v5"></path>
                    </svg>
                    Gerar
                  </button>
                </div>
              </div>
            </div>

            {/* Parcelas Customizadas - OCULTA quando é cartão com parcelas geradas */}
            {formData.installments > 1 && installmentSplits.length > 0 && !(formData.payment_type === 'card' && generatedInstallmentCardInstallments.length > 0) && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-800">
                      Valores das Parcelas (personalize se necessário)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRecalculateEqualSplits}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 flex items-center gap-1.5 transition-colors shadow-sm"
                    title="Recalcular divisão exata das parcelas com base no saldo restante"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Dividir Igualmente
                  </button>
                </div>
                
                <div className="space-y-3">
                  {installmentSplits.map((split, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 bg-white rounded-lg border border-gray-200">
                      <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Parcela {index + 1} - Valor (R$) *
                        </label>
                        <CurrencyInput
                          id={`installment_amount_${index}`}
                          value={split.amount}
                          onChange={(val) => handleInstallmentSplitChange(index, 'amount', val)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0,00"
                        />
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Parcela {index + 1} - Data *
                        </label>
                        <input
                          type="date"
                          value={split.date}
                          onChange={(e) => handleInstallmentSplitChange(index, 'date', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Parcela {index + 1} - Forma Pgto
                        </label>
                        <select
                          value={split.payment_type}
                          onChange={(e) => handleInstallmentSplitChange(index, 'payment_type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="cash">Dinheiro</option>
                          <option value="pix">PIX</option>
                          <option value="credit_card">Cartão de Crédito</option>
                          <option value="debit_card">Cartão de Débito</option>
                          <option value="bank_transfer">Transferência Bancária</option>
                          <option value="cheque">Cheque</option>
                          <option value="boleto">Boleto</option>
                        </select>
                      </div>
                      <div className="md:col-span-1 flex items-end">
                        <div className="text-xs text-gray-500 pb-2">
                          {split.amount && split.date ? (
                            <span className="text-green-600 font-medium">✓ Pronto</span>
                          ) : (
                            <span className="text-amber-600">Pendente</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Validação de soma */}
                  {(() => {
                    const splitsTotal = calculateInstallmentSplitsTotal();
                    const remaining = calculateRemainingAmount();
                    const diff = Math.abs(splitsTotal - remaining);
                    const isValid = diff <= 0.01;
                    
                    return (
                      <div className={`flex justify-between items-center p-3 rounded-lg border ${isValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-700">
                            {isValid ? '✓ Soma correta!' : '⚠️ Valor total diferente do esperado'}
                          </span>
                          {!isValid && (
                            <button
                              type="button"
                              onClick={handleRecalculateEqualSplits}
                              className="text-xs bg-red-600 text-white px-2.5 py-1 rounded hover:bg-red-700 font-medium transition-colors"
                            >
                              Corrigir Divisão
                            </button>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`text-sm font-bold ${isValid ? 'text-green-700' : 'text-red-700'}`}>
                            {formatCurrency(splitsTotal)}
                          </span>
                          {!isValid && (
                            <span className="text-xs text-red-600 block">
                              Diferença: {formatCurrency(diff)} (esperado: {formatCurrency(remaining)})
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handlePrevStep}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <ChevronLeft className="w-4 h-4" />
                Voltar
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {createMutation.isPending || updateMutation.isPending ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Indicador de progresso do wizard
  const renderStepIndicator = () => {
    const steps = [
      { num: 1, label: 'Dados Básicos' },
      { num: 2, label: 'Entrada' },
      { num: 3, label: 'Parcelas' },
    ];

    return (
      <div className="flex items-center justify-center mb-6">
        {steps.map((step, index) => (
          <React.Fragment key={step.num}>
            <div className="flex items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full font-medium text-sm transition-colors ${
                  currentStep >= step.num
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {currentStep > step.num ? (
                  <Check className="w-4 h-4" />
                ) : (
                  step.num
                )}
              </div>
              <span className={`ml-2 text-sm font-medium hidden sm:inline ${
                currentStep >= step.num ? 'text-gray-900' : 'text-gray-500'
              }`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-12 h-0.5 mx-2 ${
                  currentStep > step.num ? 'bg-green-600' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-600" />
          Contrato & Condições Financeiras
        </h3>
        <div className="flex items-center gap-2">
          {onClose && !isEditing && (
            <button
              type="button"
              onClick={() => onClose()}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
              title="Fechar"
            >
              <X className="w-4 h-4" />
              Fechar
            </button>
          )}
          {!isEditing && (
            <button
              onClick={() => {
                setFormData(prev => ({ ...prev, description: projectName || '' }));
                setIsEditing(true);
              }}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Configurar Contrato
            </button>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Defina os valores de contrato, entrada e parcelamento do projeto. As entradas e parcelas serão geradas automaticamente no módulo financeiro e na esteira de recebimentos.
      </p>

      {/* Formulário de adição/edição com wizard */}
      {isEditing && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">
              {editingBalance ? 'Editar Contrato & Parcelas' : 'Novo Contrato & Parcelas'}
            </h4>
            <button type="button" onClick={() => { resetForm(); setEditingBalance(null); }} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Indicador de progresso do wizard */}
          {renderStepIndicator()}

          {/* Renderização do passo atual */}
          {renderStep()}
        </form>
      )}

      {/* Lista de saldos */}
      {isLoading ? (
        <div className="text-center py-4 text-gray-500">Carregando...</div>
      ) : balances.length === 0 ? (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>Nenhum contrato ou condição financeira registrada para este projeto.</p>
          <p className="text-sm mt-1">Clique em "Configurar Contrato" para registrar o valor e parcelamento deste projeto.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {balances.map((balance) => (
            <div key={balance.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {user.role === 'GERENCIA' && (
                      <span className="font-medium text-gray-900">
                        {formatCurrency(balance.total_amount)}
                      </span>
                    )}
                    {getStatusBadge(balance.status)}
                    {balance.payment_condition_id && (
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                        {getPaymentConditionLabel(paymentConditions.find(c => c.id === balance.payment_condition_id))}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Ref: {formatDate(balance.reference_date)}
                    </div>
                    {balance.entry_amount > 0 && (
                      <div>Entrada: {formatCurrency(balance.entry_amount)}</div>
                    )}
                    {balance.installments > 1 && (
                      <div>{balance.installments}x</div>
                    )}
                    {balance.description && (
                      <div className="md:col-span-2 lg:col-span-4 text-gray-500">
                        {balance.description}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 ml-4">
                  <button
                    onClick={() => handleViewDetails(balance)}
                    className="p-3 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                    title="Ver Detalhes"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(balance)}
                    className="p-3 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                    title="Editar"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  {balance.entry_amount > 0 && (
                    <button
                      onClick={() => {
                        setEditingBalance(balance);
                        setShowAddSplit(true);
                      }}
                      className="p-3 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                      title="Adicionar Parcela de Entrada"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(balance.id)}
                    className="p-3 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
                    title="Deletar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulário para adicionar parcela de entrada extra */}
      {showAddSplit && editingBalance && (
        <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">
              Adicionar Parcela de Entrada Extra
            </h4>
            <button
              type="button"
              onClick={() => {
                setShowAddSplit(false);
                setNewSplit({ amount: '', date: '', payment_type: 'cash', supplier_id: null });
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Adicione uma parcela de entrada extra ao saldo de {formatCurrency(editingBalance.total_amount)}.
            Entrada atual: {formatCurrency(editingBalance.entry_amount || 0)}.
          </p>
          <form onSubmit={handleAddSplit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor (R$) *
                </label>
                <input
                  type="number"
                  value={newSplit.amount}
                  onChange={(e) => setNewSplit(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data *
                </label>
                <input
                  type="date"
                  value={newSplit.date}
                  onChange={(e) => setNewSplit(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forma de Pagamento
                </label>
                <select
                  value={newSplit.payment_type}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setNewSplit(prev => ({
                      ...prev,
                      payment_type: newValue,
                      supplier_id: newValue.endsWith('_direct') ? prev.supplier_id : null
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="cash">Dinheiro</option>
                  <option value="pix">PIX</option>
                  <option value="pix_direct">PIX direto ao fornecedor</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="debit_card">Cartão de Débito</option>
                  <option value="bank_transfer">Transferência Bancária</option>
                  <option value="cheque">Cheque</option>
                  <option value="cheque_direct">Cheque direto ao fornecedor</option>
                  <option value="boleto">Boleto</option>
                </select>
              </div>
            </div>
            {newSplit.payment_type && newSplit.payment_type.endsWith('_direct') && (
              <div className="md:col-span-3 mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fornecedor (pagamento direto) *
                </label>
                <select
                  value={newSplit.supplier_id || ''}
                  onChange={(e) => setNewSplit(prev => ({ ...prev, supplier_id: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Selecione o fornecedor</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddSplit(false);
                  setNewSplit({ amount: '', date: '', payment_type: 'cash', supplier_id: null });
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={addSplitMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                {addSplitMutation.isPending ? 'Adicionando...' : 'Adicionar Parcela'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Resumo */}
      {balances.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Total de saldos:</span>
            <span className="font-medium text-gray-900">
              {balances.length} registro(s)}{user.role === 'GERENCIA' && ` - Total: ${formatCurrency(balances.reduce((sum, b) => sum + parseFloat(b.total_amount), 0))}`}
            </span>
          </div>
        </div>
      )}

      {/* Modal de Detalhes das Entradas */}
      {detailsModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Detalhes do Saldo Inicial</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleRecalculate(detailsModal.balance.id)}
                  disabled={recalculateMutation.isPending}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${recalculateMutation.isPending ? 'animate-spin' : ''}`} />
                  {recalculateMutation.isPending ? 'Recalculando...' : 'Recalcular'}
                </button>
                <button
                  onClick={() => handleOpenAdjust(detailsModal.balance)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                >
                  <Sliders className="w-4 h-4" />
                  Ajuste
                </button>
                <button
                  onClick={() => setAbateModalOpen(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                >
                  <ArrowDownRight className="w-4 h-4" />
                  Abater Despesa
                </button>
                <button
                  onClick={() => {
                    const scheduledChequesList = Array.isArray(detailsModal.entries?.scheduled_cheques)
                      ? detailsModal.entries.scheduled_cheques
                      : [];

                    const regularExpectedList = Array.isArray(detailsModal.entries?.regular_expected)
                      ? detailsModal.entries.regular_expected
                      : (detailsModal.entries?.expected || []);

                    setReceiptModal({ open: true, data: {
                      balance: detailsModal.entries?.balance,
                      received: detailsModal.entries?.received || [],
                      expected: detailsModal.entries?.expected || [],
                      scheduled_cheques: scheduledChequesList,
                      regular_expected: regularExpectedList,
                      discounts: detailsModal.entries?.discounts || [],
                      totals: detailsModal.entries?.totals,
                      project: {
                        number: detailsModal.balance?.id ? `PROJ-${String(detailsModal.balance.id).padStart(6, '0')}` : null,
                        generatedAt: new Date().toISOString()
                      },
                      company: {
                        name: 'Santiago Inhapim Planejados',
                        tagline: 'SANTIAGO INHAPIM PLANEJADOS',
                        logo: '/logo.png'
                      },
                      clientName: clientName || projectName,
                      projectAbates: projectAbates || [],
                      extraIncomes: extraIncomes || []
                    }});
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
                >
                  <FileText className="w-4 h-4" />
                  Gerar PDF
                </button>
                <button onClick={() => setDetailsModal({ open: false, balance: null, entries: null, loading: false })} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4">
              {detailsModal.loading ? (
                <div className="text-center py-8 text-gray-500">Carregando...</div>
              ) : detailsModal.entries ? (() => {
                const scheduledChequesList = Array.isArray(detailsModal.entries.scheduled_cheques)
                  ? detailsModal.entries.scheduled_cheques
                  : [];

                const regularExpectedList = Array.isArray(detailsModal.entries.regular_expected)
                  ? detailsModal.entries.regular_expected
                  : (detailsModal.entries.expected || []);

                const totalScheduledCheques = detailsModal.entries.totals?.scheduled_cheques_total !== undefined
                  ? detailsModal.entries.totals.scheduled_cheques_total
                  : scheduledChequesList.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);

                const totalRegularExpected = regularExpectedList.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);

                // Cálculos consolidados para Total Pago e Total Pendente
                const abatesPaidAmount = projectAbates && projectAbates.length > 0
                  ? projectAbates.filter(a => a.status === 'PAID').reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0)
                  : (parseFloat(detailsModal.entries.totals?.total_abated_paid) || 0);

                const receivedFromBalance = parseFloat(detailsModal.entries.totals?.received) || 0;
                const receivedFromExtras = parseFloat(detailsModal.entries.totals?.extra_incomes_received) || 0;
                const totalPureReceived = receivedFromBalance + receivedFromExtras;

                const totalPaidAll = detailsModal.entries.totals?.total_paid !== undefined
                  ? parseFloat(detailsModal.entries.totals.total_paid)
                  : (totalPureReceived + abatesPaidAmount);

                const totalPendingAll = detailsModal.entries.totals?.total_pending !== undefined
                  ? parseFloat(detailsModal.entries.totals.total_pending)
                  : ((parseFloat(detailsModal.entries.totals?.expected) || 0) + (parseFloat(detailsModal.entries.totals?.extra_incomes_pending) || 0));

                const contractTotal = parseFloat(detailsModal.entries.balance?.total_amount) || 0;
                const totalDiscounts = parseFloat(detailsModal.entries.totals?.total_discounts) || 0;
                const extraIncomesTotal = parseFloat(detailsModal.entries.totals?.extra_incomes_total) || 0;

                // Total Geral = Contrato + Extras - Descontos (o abate é forma de pagamento, não diminui o valor global da obra)
                const grandTotal = detailsModal.entries.totals?.total_with_extras !== undefined
                  ? parseFloat(detailsModal.entries.totals.total_with_extras)
                  : (contractTotal + extraIncomesTotal - totalDiscounts);

                return (
                <>
                  {/* Resumo do saldo */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-200 space-y-3.5">
                    {/* Linha 1: Dados Contratuais da Obra */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm pb-3 border-b border-gray-200/80">
                      <div>
                        <span className="text-xs text-gray-500 font-medium">Contrato Original:</span>
                        <p className="font-bold text-gray-900 text-sm sm:text-base">{formatCurrency(detailsModal.entries.balance.total_amount)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 font-medium">Entrada:</span>
                        <p className="font-semibold text-gray-800 text-sm sm:text-base">
                          {detailsModal.entries.balance.entry_amount > 0 ? formatCurrency(detailsModal.entries.balance.entry_amount) : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 font-medium">Parcelamento:</span>
                        <p className="font-semibold text-gray-800 text-sm sm:text-base">{detailsModal.entries.balance.installments}x</p>
                      </div>
                      <div>
                        <span className="text-xs text-purple-800 font-semibold flex items-center gap-1">
                          <Receipt className="w-3.5 h-3.5 text-purple-600" /> Cheques em Carteira:
                        </span>
                        <p className="font-bold text-purple-900 text-sm sm:text-base">
                          {scheduledChequesList.length > 0 ? (
                            <span>
                              {formatCurrency(totalScheduledCheques)}{' '}
                              <span className="text-xs font-normal text-purple-600 block">
                                {scheduledChequesList.length}x • {scheduledChequesList.filter(e => e.cheque_in_hand).length} em mãos
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400 font-normal">-</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Linha 2: Cards de Execução Financeira (Total Pago, Total Pendente e Total Geral) */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Card Total Pago */}
                      <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-3.5 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between gap-1 mb-1">
                            <span className="text-xs font-bold text-emerald-800 flex items-center gap-1.5 uppercase tracking-wider">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> Total Pago
                            </span>
                            <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                              Quitado
                            </span>
                          </div>
                          <p className="text-xl sm:text-2xl font-black text-emerald-700 leading-tight">
                            {formatCurrency(totalPaidAll)}
                          </p>
                        </div>
                        <div className="mt-2 pt-2 border-t border-emerald-200/80 text-[11px] text-emerald-800 flex flex-col gap-0.5">
                          <div className="flex justify-between">
                            <span className="text-emerald-700/90">Recebido (Contrato):</span>
                            <span className="font-semibold">{formatCurrency(receivedFromBalance)}</span>
                          </div>
                          {receivedFromExtras > 0 && (
                            <div className="flex justify-between text-blue-800">
                              <span className="text-blue-700/90">Recebimentos Extras:</span>
                              <span className="font-semibold">+{formatCurrency(receivedFromExtras)}</span>
                            </div>
                          )}
                          {abatesPaidAmount > 0 && (
                            <div className="flex justify-between">
                              <span className="text-emerald-700/90">Abates de Despesa:</span>
                              <span className="font-bold text-emerald-800">+{formatCurrency(abatesPaidAmount)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Card Total Pendente */}
                      <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-3.5 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between gap-1 mb-1">
                            <span className="text-xs font-bold text-amber-800 flex items-center gap-1.5 uppercase tracking-wider">
                              <Clock className="w-4 h-4 text-amber-600 shrink-0" /> Total Pendente
                            </span>
                            <span className="text-[10px] font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full border border-amber-200">
                              A Receber
                            </span>
                          </div>
                          <p className="text-xl sm:text-2xl font-black text-amber-700 leading-tight">
                            {formatCurrency(totalPendingAll)}
                          </p>
                        </div>
                        <div className="mt-2 pt-2 border-t border-amber-200/80 text-[11px] text-amber-800 flex flex-col gap-0.5">
                          <div className="flex justify-between">
                            <span className="text-amber-700/90">Parcelas Regulares:</span>
                            <span className="font-semibold">{formatCurrency(totalRegularExpected)}</span>
                          </div>
                          {totalScheduledCheques > 0 && (
                            <div className="flex justify-between text-purple-800">
                              <span className="text-purple-700 font-medium">Cheques Agendados:</span>
                              <span className="font-bold text-purple-800">{formatCurrency(totalScheduledCheques)}</span>
                            </div>
                          )}
                          {parseFloat(detailsModal.entries.totals?.extra_incomes_pending) > 0 && (
                            <div className="flex justify-between text-orange-800">
                              <span className="text-orange-700">Extras Pendentes:</span>
                              <span className="font-semibold">{formatCurrency(detailsModal.entries.totals.extra_incomes_pending)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Card Total Geral */}
                      <div className="bg-white border border-gray-300 rounded-xl p-3.5 flex flex-col justify-between shadow-2xs">
                        <div>
                          <div className="flex items-center justify-between gap-1 mb-1">
                            <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5 uppercase tracking-wider">
                              <Coins className="w-4 h-4 text-gray-600 shrink-0" /> Total Geral da Obra
                            </span>
                            <span className="text-[10px] font-semibold bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full border border-gray-200">
                              Global
                            </span>
                          </div>
                          <p className="text-xl sm:text-2xl font-black text-gray-900 leading-tight">
                            {formatCurrency(grandTotal)}
                          </p>
                        </div>
                        <div className="mt-2 pt-2 border-t border-gray-200 text-[11px] text-gray-600 flex flex-col gap-0.5">
                          <div className="flex justify-between">
                            <span>Contrato:</span>
                            <span className="font-medium text-gray-900">{formatCurrency(detailsModal.entries.balance.total_amount)}</span>
                          </div>
                          {extraIncomesTotal > 0 && (
                            <div className="flex justify-between text-blue-700">
                              <span>Adicionais Extras:</span>
                              <span className="font-semibold">+{formatCurrency(extraIncomesTotal)}</span>
                            </div>
                          )}
                          {totalDiscounts > 0 && (
                            <div className="flex justify-between text-red-600">
                              <span>Descontos Aplicados:</span>
                              <span className="font-semibold">-{formatCurrency(totalDiscounts)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Entradas Recebidas */}
                  {detailsModal.entries.received.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        Entradas Recebidas ({detailsModal.entries.received.length})
                      </h4>
                      <div className="border rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[600px]">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-gray-600 w-24">Data</th>
                              <th className="px-3 py-2 text-left text-gray-600">Descrição</th>
                              <th className="px-3 py-2 text-left text-gray-600 w-28">Forma Pagto</th>
                              <th className="px-3 py-2 text-right text-gray-600 w-28">Valor</th>
                              <th className="px-3 py-2 text-center w-32">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailsModal.entries.received.map((entry) => (
                              <tr key={entry.id} className="border-t">
                                <td className="px-3 py-2 whitespace-nowrap">{formatDate(entry.date)}</td>
                                <td className="px-3 py-2">
                                  <div className="text-[11px] leading-relaxed break-words whitespace-normal max-w-xs md:max-w-md text-gray-800" title={entry.description}>
                                    {entry.description}
                                  </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">{getPaymentMethodLabel(entry.payment_method)}</td>
                                <td className="px-3 py-2 text-right font-medium text-green-600 whitespace-nowrap">{formatCurrency(entry.amount)}</td>
                                <td className="px-3 py-2 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => setChangeDateModal({ open: true, parcel: entry, loading: false })}
                                      className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-lg"
                                      title="Alterar Data"
                                    >
                                      <Calendar className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => setUnconfirmModal({ open: true, parcel: entry, loading: false })}
                                      className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                      title="Reverter Recebimento"
                                    >
                                      <AlertCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50 font-medium">
                            <tr>
                              <td className="px-3 py-2" colSpan={3}>Total Recebido</td>
                              <td className="px-3 py-2 text-right text-green-600">{formatCurrency(detailsModal.entries.totals.received)}</td>
                            </tr>
                          </tfoot>
                        </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Abates de Despesas */}
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <ArrowDownRight className="w-4 h-4 text-green-600" />
                      Abates de Despesas ({projectAbates.length})
                    </h4>

                    {projectAbates.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                        Nenhum abate registrado para este projeto.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {projectAbates.map((abate) => (
                          <div key={abate.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-gray-900">
                                    {formatCurrency(abate.amount)}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    abate.status === 'PAID' ? 'bg-green-100 text-green-800' :
                                    abate.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {abate.status === 'PAID' ? 'PAGO' : abate.status === 'PENDING' ? 'PENDENTE' : abate.status}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">
                                  Custo: {abate.cost_description || `ID ${abate.cost_id}`}
                                </p>
                                {abate.supplier_name && (
                                  <p className="text-sm text-gray-500">
                                    Fornecedor: {abate.supplier_name}
                                  </p>
                                )}
                                <p className="text-xs text-gray-400 mt-1">
                                  Criado em: {formatDate(abate.created_at)}
                                  {abate.payment_date && ` | Pago em: ${formatDate(abate.payment_date)}`}
                                </p>

                                {/* Show entries if any */}
                                {abate.entries && abate.entries.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <p className="text-xs font-medium text-gray-600 mb-1">Entradas Abatidas:</p>
                                    {abate.entries.map((entry) => (
                                      <div key={entry.id} className="text-xs text-gray-500 pl-2 border-l-2 border-green-300">
                                        {entry.income_description || `Entrada ${entry.expected_income_id}`}: {formatCurrency(entry.amount_abated)}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center gap-1 ml-2">
                                {abate.status === 'PENDING' && (
                                  <>
                                    <button
                                      onClick={() => markAbatePaidMutation.mutate({ id: abate.id, paymentData: { payment_date: new Date().toISOString().split('T')[0] } })}
                                      disabled={markAbatePaidMutation.isPending}
                                      className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                                      title="Marcar como pago"
                                    >
                                      {markAbatePaidMutation.isPending ? '...' : 'Pagar'}
                                    </button>
                                    <button
                                      onClick={() => setCancelAbateConfirmModal({ open: true, abateId: abate.id })}
                                      disabled={cancelAbateMutation.isPending}
                                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                      title="Cancelar"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                                {abate.status === 'PAID' && (
                                  <button
                                    onClick={() => setDeleteAbateConfirmModal({ open: true, abateId: abate.id })}
                                    disabled={cancelAbateMutation.isPending}
                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                    title="Excluir"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* FEATURE: Seção dedicada para Cheques Agendados / Pré-datados */}
                  {scheduledChequesList.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                        <h4 className="font-semibold text-purple-900 flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-purple-600" />
                          <span>Cheques Agendados / Pré-datados ({scheduledChequesList.length})</span>
                        </h4>
                        <span className="text-xs font-semibold text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-200 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-purple-600" />
                          Total em Carteira: {formatCurrency(totalScheduledCheques)}
                        </span>
                      </div>
                      <div className="border border-purple-200 rounded-lg overflow-hidden bg-purple-50/20 shadow-2xs">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm min-w-[700px]">
                            <thead className="bg-purple-50 text-purple-900 border-b border-purple-200 text-xs">
                              <tr>
                                <th className="px-3 py-2 text-left w-28 font-semibold">Data (Bom Para)</th>
                                <th className="px-3 py-2 text-left font-semibold">Descrição / Parcela</th>
                                <th className="px-3 py-2 text-left w-24 font-semibold">Forma Pagto</th>
                                <th className="px-3 py-2 text-left w-36 font-semibold">Status / Carteira</th>
                                <th className="px-3 py-2 text-center w-36 font-semibold">Cheque em Mãos</th>
                                <th className="px-3 py-2 text-right w-28 font-semibold">Valor</th>
                                <th className="px-3 py-2 text-center w-28 font-semibold">Ações</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-purple-100 bg-white">
                              {scheduledChequesList.map((entry) => (
                                <tr key={entry.id} className="hover:bg-purple-50/40 transition-colors">
                                  <td className="px-3 py-2 whitespace-nowrap font-medium text-purple-950">
                                    <span className="flex items-center gap-1.5">
                                      <Calendar className="w-3.5 h-3.5 text-purple-600" />
                                      {formatDate(entry.date)}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="text-[11px] leading-relaxed break-words whitespace-normal max-w-xs md:max-w-md text-gray-800">
                                      <span className="font-semibold text-purple-950">{entry.description}</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                                      <Receipt className="w-3.5 h-3.5 text-purple-600" />
                                      Cheque
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                      <Clock className="w-3 h-3 text-amber-600" />
                                      Pré-datado Agendado
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap text-center">
                                    <div className="inline-flex flex-col items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => toggleChequeInHandMutation.mutate({
                                          balanceId: detailsModal.balance.id,
                                          expectedIncomeId: entry.id,
                                          inHand: !entry.cheque_in_hand
                                        })}
                                        disabled={toggleChequeInHandMutation.isPending}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all cursor-pointer shadow-2xs ${
                                          entry.cheque_in_hand
                                            ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200'
                                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300'
                                        }`}
                                        title={
                                          entry.cheque_in_hand
                                            ? 'Cheque físico já está em mãos da empresa! Clique para desmarcar se necessário.'
                                            : 'Clique para marcar que este cheque já foi recebido fisicamente em mãos.'
                                        }
                                      >
                                        {entry.cheque_in_hand ? (
                                          <>
                                            <Check className="w-3.5 h-3.5 text-emerald-700" />
                                            <span>Em Mãos ✓</span>
                                          </>
                                        ) : (
                                          <>
                                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                                            <span>Aguardando</span>
                                          </>
                                        )}
                                      </button>
                                      {entry.cheque_in_hand && entry.cheque_received_at && (
                                        <span className="text-[10px] text-emerald-700 font-medium whitespace-nowrap flex items-center gap-0.5" title={`Data em que foi recebido: ${formatDate(entry.cheque_received_at)}`}>
                                          Recebido: {formatDate(entry.cheque_received_at)}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-right font-bold text-purple-800 whitespace-nowrap">
                                    {formatCurrency(entry.amount)}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <button
                                        onClick={() => setChangeDateModal({ open: true, parcel: entry, loading: false })}
                                        className="p-1.5 text-purple-600 hover:text-purple-800 hover:bg-purple-100 rounded-lg transition-colors"
                                        title="Alterar data de compensação do cheque"
                                      >
                                        <Calendar className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          setReceiptPaymentMethod('cheque');
                                          setCompensateModal({ open: true, parcel: entry });
                                        }}
                                        disabled={confirmReceiptMutation.isPending}
                                        className="px-2.5 py-1 bg-purple-700 hover:bg-purple-800 text-white text-xs font-semibold rounded-lg transition-colors shadow-2xs"
                                        title="Compensar este cheque e lançar entrada no Caixa da Empresa"
                                      >
                                        Compensar
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-purple-50/80 font-semibold border-t border-purple-200 text-purple-950">
                              <tr>
                                <td className="px-3 py-2" colSpan={5}>
                                  Total em Cheques Agendados ({scheduledChequesList.length}x • {scheduledChequesList.filter(e => e.cheque_in_hand).length} em mãos)
                                </td>
                                <td className="px-3 py-2 text-right text-purple-800">{formatCurrency(totalScheduledCheques)}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Entradas Previstas (Parcelas Restantes / Regulares) */}
                  {regularExpectedList.length > 0 ? (
                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-yellow-600" />
                          <span>Entradas Previstas ({regularExpectedList.length})</span>
                        </h4>
                        {(() => {
                          const hasPendingCard = regularExpectedList.some(e => {
                            const pm = (e.payment_method || '').toUpperCase();
                            const desc = (e.description || '').toUpperCase();
                            return pm === 'CARTAO' || pm === 'CARD' || desc.includes('(CARD)') || desc.includes('CARTÃO') || desc.includes('CARTAO');
                          });
                          if (!hasPendingCard) return null;
                          return (
                            <button
                              type="button"
                              onClick={() => setConfirmAllCardModalOpen(true)}
                              disabled={confirmAllCardMutation.isPending}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 disabled:opacity-50 self-start sm:self-auto cursor-pointer"
                              title="Abre a confirmação detalhada para liquidar todas as parcelas em cartão pendentes de uma só vez"
                            >
                              <CreditCard className="w-3.5 h-3.5" />
                              <span>{confirmAllCardMutation.isPending ? 'Confirmando...' : 'Confirmar Todas as Parcelas do Cartão'}</span>
                            </button>
                          );
                        })()}
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[700px]">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-gray-600 w-24">Data</th>
                              <th className="px-3 py-2 text-left text-gray-600">Descrição</th>
                              <th className="px-3 py-2 text-left text-gray-600 w-28">Modalidade</th>
                              <th className="px-3 py-2 text-left text-gray-600 w-28">Forma Pagto</th>
                              <th className="px-3 py-2 text-left text-gray-600 w-20">Status</th>
                              <th className="px-3 py-2 text-right text-gray-600 w-28">Valor</th>
                              <th className="px-3 py-2 text-center text-gray-600 w-20">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {regularExpectedList.map((entry) => (
                              <tr key={entry.id} className="border-t">
                                <td className="px-3 py-2 whitespace-nowrap">{formatDate(entry.date)}</td>
                                <td className="px-3 py-2">
                                  <div className="text-[11px] leading-relaxed break-words whitespace-normal max-w-xs md:max-w-md text-gray-800" title={entry.description}>
                                    {entry.description}
                                  </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {entry.is_fractioned ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                      Saldo Restante (Fracionada)
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700">
                                      Regular
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">{getPaymentMethodLabel(entry.payment_method)}</td>
                                <td className="px-3 py-2">
                                  {(() => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const entryDate = new Date(entry.date);
                                    entryDate.setHours(0, 0, 0, 0);
                                    const isOverdue = entry.status === 'PENDING' && entryDate < today;
                                    return (
                                      <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                                        isOverdue ? 'bg-red-100 text-red-800 animate-pulse' :
                                        entry.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                        entry.status === 'PARTIAL' ? 'bg-blue-100 text-blue-800' :
                                        'bg-green-100 text-green-800'
                                      }`}>
                                        {isOverdue ? 'Atrasado' : entry.status === 'PENDING' ? 'Pendente' : entry.status === 'PARTIAL' ? 'Parcial' : 'Completo'}
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td className="px-3 py-2 text-right font-medium whitespace-nowrap">
                                  {entry.has_discount ? (
                                    <div className="flex flex-col items-right">
                                      <span className="text-red-400 line-through text-xs">{formatCurrency(entry.amount + entry.discount_amount)}</span>
                                      <span className="text-yellow-600" title={`Desconto de ${formatCurrency(entry.discount_amount)} aplicado`}>
                                        {formatCurrency(entry.amount)}
                                        <Tag className="w-3 h-3 inline ml-1 text-green-600" />
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-yellow-600">{formatCurrency(entry.amount)}</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {entry.status === 'PENDING' && (
                                    <div className="flex items-center justify-center gap-1">
                                      {/* Atalho para marcar que o cheque já está em mãos diretamente na esteira de previstas */}
                                      {((entry.payment_method || '').toUpperCase() === 'CHEQUE' || (entry.description || '').toUpperCase().includes('CHEQUE')) && (
                                        <button
                                          type="button"
                                          onClick={() => toggleChequeInHandMutation.mutate({
                                            balanceId: detailsModal.balance.id,
                                            expectedIncomeId: entry.id,
                                            inHand: true
                                          })}
                                          disabled={toggleChequeInHandMutation.isPending}
                                          className="p-1.5 text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors flex items-center gap-1 text-[11px] font-semibold"
                                          title="Marcar que o cheque desta parcela já foi entregue pelo cliente (vai para a carteira de cheques em mãos)"
                                        >
                                          <Receipt className="w-3.5 h-3.5 text-purple-600" />
                                          <span className="hidden xl:inline">Em Mãos</span>
                                        </button>
                                      )}
                                      <button
                                        onClick={() => setDiscountModal({ open: true, parcel: entry, loading: false })}
                                        className="p-2 min-w-[38px] min-h-[38px] flex items-center justify-center text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg"
                                        title="Aplicar Desconto"
                                      >
                                        <Tag className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          const desc = (entry.description || '').toUpperCase();
                                          let initialMethod = 'pix';
                                          if (desc.includes('CHEQUE')) {
                                            initialMethod = 'cheque';
                                          } else if (entry.payment_method) {
                                            const pm = String(entry.payment_method).toLowerCase();
                                            if (pm.includes('cheque')) initialMethod = 'cheque';
                                            else if (pm.includes('cart') || pm.includes('card')) initialMethod = 'credit_card';
                                            else if (pm.includes('bol')) initialMethod = 'boleto';
                                            else if (pm.includes('dinheiro') || pm.includes('cash')) initialMethod = 'cash';
                                            else if (pm.includes('transf')) initialMethod = 'bank_transfer';
                                          }
                                          setReceiptPaymentMethod(initialMethod);
                                          setPartialReceiptModal({ open: true, parcel: entry, loading: false });
                                        }}
                                        disabled={confirmReceiptMutation.isPending}
                                        className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 min-h-[38px]"
                                      >
                                        Confirmar
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50 font-medium">
                            <tr>
                              <td className="px-3 py-2" colSpan={5}>Total Previsto Regular</td>
                              <td className="px-3 py-2 text-right text-yellow-600">{formatCurrency(totalRegularExpected)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                        </div>
                      </div>
                    </div>
                  ) : scheduledChequesList.length > 0 ? (
                    <div className="p-4 bg-purple-50/50 rounded-lg border border-purple-200 text-xs text-purple-800 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-purple-600 shrink-0" />
                      <span>Todas as parcelas restantes deste contrato já estão vinculadas a Cheques Agendados em carteira acima.</span>
                    </div>
                  ) : null}

                  {detailsModal.entries.received.length === 0 && detailsModal.entries.expected.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      Nenhuma entrada gerada para este saldo inicial.
                    </div>
                  )}

                  {/* Recebimentos Extras */}
                  {detailsModal.entries.extra_incomes && detailsModal.entries.extra_incomes.length > 0 && (
                    <div className="mt-6 border-t pt-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Coins className="w-4 h-4 text-yellow-600" />
                        Recebimentos Extras ({detailsModal.entries.extra_incomes.length})
                      </h4>
                      <div className="border rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[600px]">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-gray-600">Descrição</th>
                              <th className="px-3 py-2 text-left text-gray-600 w-20">Parcelas</th>
                              <th className="px-3 py-2 text-left text-gray-600 w-20">Status</th>
                              <th className="px-3 py-2 text-right text-gray-600 w-28">Valor Total</th>
                              <th className="px-3 py-2 text-right text-gray-600 w-28">Recebido</th>
                              <th className="px-3 py-2 text-right text-gray-600 w-28">Pendente</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailsModal.entries.extra_incomes.map((extra) => (
                              <tr key={extra.id} className="border-t">
                                <td className="px-3 py-2">
                                  <span className="block sm:max-w-xs sm:truncate" title={extra.description}>
                                    {extra.description}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">{extra.installments}x</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    extra.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                    extra.status === 'PARTIAL' ? 'bg-blue-100 text-blue-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {extra.status === 'COMPLETED' ? 'Completo' : extra.status === 'PARTIAL' ? 'Parcial' : 'Pendente'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right font-medium text-yellow-600 whitespace-nowrap">
                                  {formatCurrency(extra.total_amount)}
                                </td>
                                <td className="px-3 py-2 text-right font-medium text-green-600 whitespace-nowrap">
                                  {formatCurrency(extra.received_amount)}
                                </td>
                                <td className="px-3 py-2 text-right font-medium text-orange-600 whitespace-nowrap">
                                  {formatCurrency(extra.pending_amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50 font-medium">
                            <tr>
                              <td className="px-3 py-2" colSpan={3}>Totais Extras</td>
                              <td className="px-3 py-2 text-right text-yellow-600">{formatCurrency(detailsModal.entries.totals.extra_incomes_total || 0)}</td>
                              <td className="px-3 py-2 text-right text-green-600">{formatCurrency(detailsModal.entries.totals.extra_incomes_received || 0)}</td>
                              <td className="px-3 py-2 text-right text-orange-600">{formatCurrency(detailsModal.entries.totals.extra_incomes_pending || 0)}</td>
                            </tr>
                          </tfoot>
                        </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })() : (
              <div className="text-center py-8 text-gray-500">Erro ao carregar dados.</div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Dedicado de Compensação Manual de Cheque em Mãos */}
      {compensateModal.open && compensateModal.parcel && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100">
            <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-purple-800 to-indigo-800 text-white">
              <h3 className="text-base font-bold flex items-center gap-2">
                <Receipt className="w-5 h-5 text-purple-200" />
                <span>Compensar Cheque Manualmente</span>
              </h3>
              <button
                type="button"
                onClick={() => setCompensateModal({ open: false, parcel: null })}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-purple-50 rounded-xl p-3.5 border border-purple-200 space-y-1">
                <p className="text-xs text-purple-900 font-medium">
                  <strong>Parcela / Cheque:</strong> {compensateModal.parcel.description}
                </p>
                <p className="text-xs text-purple-900 font-medium">
                  <strong>Data Original (Bom Para):</strong> {formatDate(compensateModal.parcel.date)}
                </p>
                <p className="text-xs text-purple-950 font-bold pt-1 border-t border-purple-200 flex justify-between">
                  <span>Valor do Cheque:</span>
                  <span className="text-sm text-purple-900">{formatCurrency(parseFloat(compensateModal.parcel.expected_amount || compensateModal.parcel.amount))}</span>
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Data Efetiva da Compensação Bancária *
                </label>
                <input
                  type="date"
                  id="chequeCompensateDate"
                  defaultValue={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs text-gray-900 focus:ring-2 focus:ring-purple-500 bg-white"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Esta será a data oficial de entrada lançada no Caixa da Empresa.
                </p>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span>
                  Ao confirmar, este cheque sairá da carteira de cheques agendados e gerará imediatamente uma <strong>Entrada de Caixa</strong> no valor integral de <strong>{formatCurrency(parseFloat(compensateModal.parcel.expected_amount || compensateModal.parcel.amount))}</strong>.
                </span>
              </div>

              <div className="flex gap-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setCompensateModal({ open: false, parcel: null })}
                  className="flex-1 px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const compDate = document.getElementById('chequeCompensateDate').value || new Date().toISOString().split('T')[0];
                    const amountVal = parseFloat(compensateModal.parcel.expected_amount || compensateModal.parcel.amount);
                    const uniqueIdempotencyKey = `compensate_${compensateModal.parcel.id}_${Date.now()}`;

                    confirmParcelRefactoredMutation.mutate({
                      balanceId: detailsModal.balance.id,
                      expectedIncomeId: compensateModal.parcel.id,
                      receivedAmount: amountVal,
                      paymentDate: compDate,
                      paymentMethod: 'cheque',
                      idempotencyKey: uniqueIdempotencyKey
                    }, {
                      onSuccess: () => {
                        setCompensateModal({ open: false, parcel: null });
                      }
                    });
                  }}
                  disabled={confirmParcelRefactoredMutation.isPending}
                  className="flex-1 px-4 py-2 text-xs font-bold text-white bg-purple-700 hover:bg-purple-800 rounded-xl shadow-xs transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  {confirmParcelRefactoredMutation.isPending ? 'Compensando...' : 'Confirmar Compensação'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE 02: Modal de Recebimento Parcial */}
      {partialReceiptModal.open && partialReceiptModal.parcel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Confirmar Recebimento</h3>
              <button onClick={() => setPartialReceiptModal({ open: false, parcel: null, loading: false })} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Parcela:</strong> {partialReceiptModal.parcel.description || `Parcela #${partialReceiptModal.parcel.installment_number}`}
                </p>
                <p className="text-sm text-blue-800">
                  <strong>Valor Original:</strong> {formatCurrency(parseFloat(partialReceiptModal.parcel.original_amount || partialReceiptModal.parcel.amount))}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor Recebido</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                  <input
                    type="text"
                    id="receivedAmount"
                    defaultValue={formatBRL(partialReceiptModal.parcel.original_amount || partialReceiptModal.parcel.amount)}
                    onBlur={(e) => {
                      const numValue = parseFormattedCurrency(e.target.value);
                      e.target.value = formatBRL(numValue);
                      document.getElementById('receivedAmount').dataset.value = numValue;
                      const expectedAmount = parseFloat(partialReceiptModal.parcel.original_amount) || parseFloat(partialReceiptModal.parcel.amount);
                      setHasAmountDifference(numValue !== expectedAmount);
                    }}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div id="differenceAlert" className="hidden bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Diferença: </strong>
                  <span id="differenceValue">R$ 0,00</span>
                </p>
                <p className="text-xs text-yellow-600 mt-1" id="differenceType"></p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data do Recebimento</label>
                <input
                  type="date"
                  id="paymentDate"
                  defaultValue={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
                <select
                  value={receiptPaymentMethod}
                  onChange={(e) => setReceiptPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="pix">PIX</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="debit_card">Cartão de Débito</option>
                  <option value="bank_transfer">Transferência Bancária</option>
                  <option value="cheque">Cheque</option>
                  <option value="boleto">Boleto</option>
                  <option value="cash">Dinheiro</option>
                </select>
              </div>

              {hasAmountDifference && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="activateFractioning"
                    checked={activateFractioning}
                    onChange={(e) => setActivateFractioning(e.target.checked)}
                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                  />
                  <label htmlFor="activateFractioning" className="text-sm text-gray-700">
                    Ativar Fracionamento (parcela não será afetada por ajustes)
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setPartialReceiptModal({ open: false, parcel: null, loading: false });
                    setActivateFractioning(false);
                    setHasAmountDifference(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    const inputEl = document.getElementById('receivedAmount');
                    const storedValue = inputEl.dataset.value;
                    const receivedAmount = storedValue ? parseFloat(storedValue) : parseFormattedCurrency(inputEl.value);
                    const paymentDate = document.getElementById('paymentDate').value;
                    if (receivedAmount <= 0) {
                      toast.error('Valor deve ser maior que zero');
                      return;
                    }
                    // Gerar chave de idempotência única para permitir múltiplos pagamentos parciais legítimos (ex: dois cheques de R$ 5.000)
                    const uniqueIdempotencyKey = `confirm_${partialReceiptModal.parcel.id}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

                    confirmParcelRefactoredMutation.mutate({
                      balanceId: detailsModal.balance.id,
                      expectedIncomeId: partialReceiptModal.parcel.id,
                      receivedAmount,
                      paymentDate,
                      paymentMethod: receiptPaymentMethod,
                      activate_fractioning: activateFractioning,
                      idempotencyKey: uniqueIdempotencyKey
                    });
                  }}
                  disabled={confirmParcelRefactoredMutation.isPending}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {confirmParcelRefactoredMutation.isPending ? 'Confirmando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE 01: Modal de Desconfirmação */}
      {unconfirmModal.open && unconfirmModal.parcel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Reverter Recebimento</h3>
              <button onClick={() => setUnconfirmModal({ open: false, parcel: null, loading: false })} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>ATENÇÃO:</strong> Esta ação irá estornar o recebimento da parcela.
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-800">
                  <strong>Parcela:</strong> {unconfirmModal.parcel.description || `Parcela #${unconfirmModal.parcel.installment_number}`}
                </p>
                <p className="text-sm text-gray-800">
                  <strong>Valor:</strong> {formatCurrency(parseFloat(unconfirmModal.parcel.amount_received || unconfirmModal.parcel.gross_amount || 0))}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da Reversão *</label>
                <textarea
                  id="unconfirmReason"
                  rows={3}
                  placeholder="Ex: Cliente devolveu o pagamento"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Data de Vencimento *</label>
                <input
                  type="date"
                  id="unconfirmNewDueDate"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setUnconfirmModal({ open: false, parcel: null, loading: false })}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    const reason = document.getElementById('unconfirmReason').value;
                    const newDueDate = document.getElementById('unconfirmNewDueDate').value;
                    if (!reason.trim()) {
                      toast.error('Motivo é obrigatório');
                      return;
                    }
                    if (!newDueDate) {
                      toast.error('Nova data de vencimento é obrigatória');
                      return;
                    }
                    unconfirmReceiptMutation.mutate({
                      balanceId: detailsModal.balance.id,
                      incomeId: unconfirmModal.parcel.id,
                      reason,
                      newDueDate
                    });
                  }}
                  disabled={unconfirmReceiptMutation.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {unconfirmReceiptMutation.isPending ? 'Revertendo...' : 'Reverter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE 03: Modal de Alteração de Data */}
      {changeDateModal.open && changeDateModal.parcel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Alterar Data de Vencimento</h3>
              <button onClick={() => setChangeDateModal({ open: false, parcel: null, loading: false })} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-800">
                  <strong>Parcela:</strong> {changeDateModal.parcel.description || `Parcela #${changeDateModal.parcel.installment_number}`}
                </p>
                <p className="text-sm text-gray-800">
                  <strong>Data Atual:</strong> {formatDate(changeDateModal.parcel.expected_date || changeDateModal.parcel.date)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Data de Vencimento</label>
                <input
                  type="date"
                  id="newDueDate"
                  defaultValue={(() => {
                    const raw = changeDateModal.parcel.expected_date || changeDateModal.parcel.date;
                    if (!raw) return '';
                    return String(raw).split('T')[0];
                  })()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (opcional)</label>
                <textarea
                  id="changeDateReason"
                  rows={2}
                  placeholder="Ex: Cliente solicitou mudança"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setChangeDateModal({ open: false, parcel: null, loading: false })}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    const newDueDate = document.getElementById('newDueDate').value;
                    const reason = document.getElementById('changeDateReason').value;
                    if (!newDueDate) {
                      toast.error('Nova data é obrigatória');
                      return;
                    }
                    const resolvedIncomeId = changeDateModal.parcel.expected_income_id || changeDateModal.parcel.id;
                    if (!resolvedIncomeId) {
                      toast.error('Identificador da parcela não encontrado');
                      return;
                    }
                    changeDueDateMutation.mutate({
                      balanceId: detailsModal.balance.id,
                      expectedIncomeId: resolvedIncomeId,
                      newDueDate,
                      reason
                    });
                  }}
                  disabled={changeDueDateMutation.isPending}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {changeDueDateMutation.isPending ? 'Alterando...' : 'Alterar Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FEATURE: Modal de Desconto */}
      {discountModal.open && discountModal.parcel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Aplicar Desconto</h3>
              <button onClick={() => setDiscountModal({ open: false, parcel: null, loading: false })} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>Parcela:</strong> {discountModal.parcel.description || `Parcela #${discountModal.parcel.installment_number}`}
                </p>
                <p className="text-sm text-green-800">
                  <strong>Valor Atual:</strong> {formatCurrency(parseFloat(discountModal.parcel.amount || discountModal.parcel.expected_amount))}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor do Desconto (R$)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                  <input
                    type="text"
                    id="discountAmount"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0,00"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setDiscountModal({ open: false, parcel: null, loading: false })}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    const inputEl = document.getElementById('discountAmount');
                    const discountValue = parseFormattedCurrency(inputEl.value);
                    const currentAmount = parseFloat(discountModal.parcel.amount || discountModal.parcel.expected_amount);
                    
                    if (!discountValue || discountValue <= 0) {
                      toast.error('Informe um valor de desconto maior que zero');
                      return;
                    }
                    if (discountValue > currentAmount) {
                      toast.error(`Desconto não pode ser maior que o valor da parcela (${formatCurrency(currentAmount)})`);
                      return;
                    }
                    
                    applyDiscountMutation.mutate({
                      balanceId: detailsModal.balance.id,
                      expectedIncomeId: discountModal.parcel.id,
                      discountAmount: discountValue
                    });
                  }}
                  disabled={applyDiscountMutation.isPending}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {applyDiscountMutation.isPending ? 'Aplicando...' : 'Aplicar Desconto'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Abate de Despesa */}
      <ExpenseAbatementModal
        isOpen={abateModalOpen}
        onClose={() => setAbateModalOpen(false)}
        projectId={projectId}
        projectCode={balances.find(b => b.id === detailsModal.balance?.id)?.project_id ? `PROJ-${projectId}` : `PROJ-${projectId}`}
        clientName={projectName}
        balance={detailsModal.balance}
        onRefresh={fetchProjectAbates}
      />

      {/* Modal de Ajuste de Divergência */}
      {adjustModal.open && adjustModal.balance && detailsModal.entries && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Sliders className="w-5 h-5 text-purple-600" />
                Ajuste de Divergência
              </h3>
              <button onClick={() => setAdjustModal({ open: false, balance: null, loading: false })} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  Este ajuste redistribuirá os valores das parcelas pendentes para corrigir 
                  a diferença entre o total, o valor recebido e o esperado.
                </p>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total do Saldo:</span>
                  <span className="font-medium">{formatCurrency(detailsModal.entries.balance.total_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Recebido (Saldo):</span>
                  <span className="font-medium text-green-600">{formatCurrency((detailsModal.entries.totals.received || 0) + (detailsModal.entries.totals.total_abated || 0))}</span>
                </div>
                {detailsModal.entries.totals.extra_incomes_received > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Recebido (Extras):</span>
                    <span className="font-medium text-green-600">{formatCurrency(detailsModal.entries.totals.extra_incomes_received || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600 font-medium">Total Recebido:</span>
                  <span className="font-bold text-green-600">{formatCurrency(detailsModal.entries.totals.received_with_extras || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Pendente (Saldo):</span>
                  <span className="font-medium text-yellow-600">{formatCurrency(detailsModal.entries.totals.expected)}</span>
                </div>
                {detailsModal.entries.totals.extra_incomes_pending > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Pendente (Extras):</span>
                    <span className="font-medium text-orange-600">{formatCurrency(detailsModal.entries.totals.extra_incomes_pending || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600 font-medium">Total Pendente:</span>
                  <span className="font-bold text-orange-600">{formatCurrency(detailsModal.entries.totals.pending_with_extras || 0)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600 font-medium">Diferença:</span>
                  {(() => {
                    // Considerar também os descontos aplicados
                    const totalDiscounts = detailsModal.entries.totals.total_discounts || 0;
                    const received = detailsModal.entries.totals.received || 0;
                    const totalAbated = detailsModal.entries.totals.total_abated || 0;
                    const expected = detailsModal.entries.totals.expected || 0;
                    const totalAmount = detailsModal.entries.balance.total_amount;

                    // Fórmula correta: diff = total - discounts - received - abated - expected
                    const diff = totalAmount - totalDiscounts - received - totalAbated - expected;
                    if (Math.abs(diff) < 0.01) {
                      return <span className="font-bold text-green-600">Sem divergências</span>;
                    }
                    return (
                      <span className={`font-bold ${diff >= 0 ? 'text-red-600' : 'text-blue-600'}`}>
                        {formatCurrency(Math.abs(diff))} {diff >= 0 ? 'faltando' : 'sobrando'}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600 font-medium">Parcelas Pendentes:</span>
                  <span className="font-medium">{detailsModal.entries.expected.length}x</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600 font-medium">Novo Valor por Parcela:</span>
                  <span className="font-bold text-purple-600">
                    {detailsModal.entries.expected.length > 0 ? (
                      formatCurrency(
                        (detailsModal.entries.totals.expected +
                          (detailsModal.entries.balance.total_amount - (detailsModal.entries.totals.received || 0) - (detailsModal.entries.totals.total_abated || 0) - (detailsModal.entries.totals.total_discounts || 0) - detailsModal.entries.totals.expected))
                        / detailsModal.entries.expected.length
                      )
                    ) : (
                      <span className="text-green-600">Quitado</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button
                onClick={() => setAdjustModal({ open: false, balance: null, loading: false })}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                disabled={adjustModal.loading}
              >
                Cancelar
              </button>
              <button
                onClick={handleAdjust}
                disabled={adjustModal.loading || Math.abs(
                  detailsModal.entries.balance.total_amount -
                  (detailsModal.entries.totals.total_discounts || 0) -
                  (detailsModal.entries.totals.received || 0) -
                  (detailsModal.entries.totals.total_abated || 0) -
                  detailsModal.entries.totals.expected
                ) < 0.01}
                className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:opacity-50 disabled:bg-gray-400"
              >
                {adjustModal.loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Ajustando...
                  </>
                ) : Math.abs(detailsModal.entries.balance.total_amount - (detailsModal.entries.totals.received || 0) - (detailsModal.entries.totals.total_abated || 0) - detailsModal.entries.totals.expected) < 0.01 ? (
                  <>
                    <Check className="w-4 h-4" />
                    Tudo Correto
                  </>
                ) : (
                  <>
                    <Sliders className="w-4 h-4" />
                    Confirmar Ajuste
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {receiptModal.open && receiptModal.data && (
        <InitialBalanceReceiptModal
          data={receiptModal.data}
          onClose={() => setReceiptModal({ open: false, data: null })}
        />
      )}

      <ConfirmModal
        show={deleteConfirmModal.open}
        isOpen={deleteConfirmModal.open}
        onCancel={() => setDeleteConfirmModal({ open: false, balanceId: null })}
        onClose={() => setDeleteConfirmModal({ open: false, balanceId: null })}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja deletar este contrato? Esta ação fará o estorno correspondente no Caixa da Empresa e removerá as parcelas pendentes do contas a receber."
      />

      <ConfirmModal
        show={cancelAbateConfirmModal.open}
        isOpen={cancelAbateConfirmModal.open}
        onCancel={() => setCancelAbateConfirmModal({ open: false, abateId: null })}
        onClose={() => setCancelAbateConfirmModal({ open: false, abateId: null })}
        onConfirm={() => {
          cancelAbateMutation.mutate(cancelAbateConfirmModal.abateId);
          setCancelAbateConfirmModal({ open: false, abateId: null });
        }}
        title="Confirmar Cancelamento"
        message="Tem certeza que deseja cancelar este abate?"
      />

      <ConfirmModal
        show={deleteAbateConfirmModal.open}
        isOpen={deleteAbateConfirmModal.open}
        onCancel={() => setDeleteAbateConfirmModal({ open: false, abateId: null })}
        onClose={() => setDeleteAbateConfirmModal({ open: false, abateId: null })}
        onConfirm={() => {
          cancelAbateMutation.mutate(deleteAbateConfirmModal.abateId);
          setDeleteAbateConfirmModal({ open: false, abateId: null });
        }}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este abate? Os valores serão revertidos para as parcelas e as transações de caixa serão removidas."
      />

      {/* Modal de Confirmação Detalhada para Liquidação em Lote de Cartão */}
      {confirmAllCardModalOpen && detailsModal.open && detailsModal.balance && (() => {
        const pendingCardParcels = (detailsModal.entries?.expected || []).filter(e => {
          const pm = (e.payment_method || '').toUpperCase();
          const desc = (e.description || '').toUpperCase();
          return pm === 'CARTAO' || pm === 'CARD' || desc.includes('(CARD)') || desc.includes('CARTÃO') || desc.includes('CARTAO');
        });
        const totalPendingCardAmount = pendingCardParcels.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

        return (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-gray-100">
              {/* Cabeçalho */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-emerald-50/70">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-100 text-emerald-800 rounded-xl">
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Confirmar Todas as Parcelas do Cartão
                    </h3>
                    <p className="text-xs text-gray-500">
                      Liquidação antecipada em lote de venda parcelada na maquininha
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmAllCardModalOpen(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Corpo com Detalhes */}
              <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                {/* Resumo do Contrato */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Contrato / Projeto</span>
                      <h4 className="text-sm font-bold text-gray-900">
                        {projectName || detailsModal.balance.description || `Projeto #${projectId}`}
                      </h4>
                      {clientName && (
                        <p className="text-xs text-gray-600 mt-0.5">
                          Cliente: <strong className="text-gray-800">{clientName}</strong>
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Total a Liquidar</span>
                      <p className="text-xl font-extrabold text-emerald-700 font-mono">
                        {formatCurrency(totalPendingCardAmount)}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
                    <span>Quantidade de Parcelas:</span>
                    <span className="font-bold text-gray-900">
                      {pendingCardParcels.length} parcela(s) pendente(s)
                    </span>
                  </div>
                </div>

                {/* Lista de Parcelas que serão confirmadas */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Parcelas Incluídas na Liquidação:
                  </label>
                  <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-xl p-2 bg-gray-50/50 space-y-1">
                    {pendingCardParcels.map((parcel) => (
                      <div
                        key={parcel.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-200 text-xs"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span className="font-semibold text-gray-800 truncate">{parcel.description}</span>
                          <span className="text-[11px] text-gray-400">({formatDate(parcel.date)})</span>
                        </div>
                        <span className="font-bold text-gray-900 font-mono shrink-0 ml-2">
                          {formatCurrency(parcel.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Caixa Explicativa: O que esse botão fará */}
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-2.5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-900 flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-emerald-700" />
                    <span>O que este botão irá fazer:</span>
                  </h4>
                  <ul className="text-xs text-emerald-950 space-y-1.5 leading-relaxed pl-1">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-emerald-700">1.</span>
                      <span><strong>Liquidação Integral do Cartão:</strong> Muda o status de todas as {pendingCardParcels.length} parcelas pendentes para <strong>RECEBIDO</strong>, oficializando que a venda em 10x foi autorizada na maquininha.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-emerald-700">2.</span>
                      <span><strong>Lançamento no Livro Caixa:</strong> Cria as movimentações de <strong>ENTRADA (INCOME)</strong> no Caixa da Empresa, creditando o valor no saldo financeiro da empresa.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-emerald-700">3.</span>
                      <span><strong>Receitas Contábeis:</strong> Registra os lançamentos oficiais de recebimento na tabela <code>incomes</code>.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-emerald-700">4.</span>
                      <span><strong>Conclusão do Contrato:</strong> Atualiza o status geral do saldo inicial para <strong>CONCLUÍDO (COMPLETED)</strong>, eliminando qualquer pendência financeira deste contrato.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Rodapé com Ações */}
              <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-100 bg-gray-50/70">
                <button
                  type="button"
                  onClick={() => setConfirmAllCardModalOpen(false)}
                  disabled={confirmAllCardMutation.isPending}
                  className="px-4 py-2.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => confirmAllCardMutation.mutate({ balanceId: detailsModal.balance.id })}
                  disabled={confirmAllCardMutation.isPending}
                  className="px-5 py-2.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                >
                  {confirmAllCardMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Liquidando Todas as Parcelas...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      <span>Sim, Confirmar e Liquidar Tudo</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ProjectInitialBalances;
